/**
 * The single renderer used by the editor and the exporter, so what you see is
 * exactly what you export. All drawing happens in image pixel space; callers
 * set the context transform (viewport scale/translate) beforehand.
 */
import { flatPath, flatten } from './flat.ts';
import type { Callout, Elevation, Form, Highlight, MapDoc, Note, Shape, TestPoint, WorkArea } from '../../shared/doc.ts';
import { formHandles, formMids, formRuns, type PolyForm } from '../editor/form.ts';
import type { GuideSnap } from '../editor/pen.ts';
import { keyVertices, roundedPolygonPath, shapeToPolygon } from '../editor/outline.ts';
import { pathOn } from '../editor/curve.ts';
import { placementOf } from '../../shared/doc.ts';
import { FAIL_LABEL } from '../model/markups.ts';
import { add, dist, mul, polyBounds, rectToPoly, type Rect } from '../editor/geom.ts';
import { leaderGeometry as leaderGeom, type LeaderGeom } from '../editor/layout.ts';
import { AREA_FILL_ALPHA, BAND_ALPHA, EDGE_ALPHA, FAIL, FAIL_AREA_FILL, INK, LEGEND_BORDER, PAPER, PASS, areaColors, darkOf, elevColor, elevInk, fontString, materialColor, mixHex, noteColor, type Metrics } from './style.ts';
import type { BandEnd } from '../editor/ends.ts';
import type { DotSize, LabelBox, Layout, LegendLayout } from './labels.ts';
import { HEAD_RUN, easedRadii, type LeaderRoute } from '../editor/callout.ts';
import { areaStroke, calloutColor, labelRadius, legendLayout, shapeBounds, sharedLabelColor } from './labels.ts';
import { createMeasurer } from './text.ts';
import { drawRich } from './rich.ts';
import type { SoilsLeader } from '../editor/soils.ts';
import { liftedPlan } from './lift.ts';
import { cutRegions, cutsOf } from '../editor/cuts.ts';

export interface RenderOptions {
  /** Ids of selected items (editor only). */
  selection?: ReadonlySet<string>;
  /** Draw editor-only affordances (selection glow). */
  interactive?: boolean;
  /** Screen px per image px, so editor-only affordances keep a constant size on screen. */
  viewScale?: number;
  /** The selection glow this frame (at rest when omitted). */
  glow?: GlowState;
  /**
   * The callout box (or soils label) in the hand: `lift` (0 … 1) raises it off the plan on a soft
   * shadow; `adrift` (0 … 1) is how far it has let go — over a place it may not rest its leaders
   * fade away and its colour drains towards grey, so it plainly cannot be put down there. `dpr`:
   * device px per CSS px.
   */
  carried?: { id: string; lift: number; adrift: number; dpr: number };
  /**
   * The arrowhead in the hand (a callout's by its place in `arrowsOf`, a shared label's by its area's
   * id): its selection ring drawn stronger while it is dragged.
   */
  heldArrow?: { id: string; arrow: number | string };
  /**
   * The selected box whose width is for the hand (`Editor.selectedBox`): its grips are drawn on the
   * middle of its sides, on its selection ring — the one in the hand (`held`, 0 left, 1 right) lit.
   */
  grip?: { rect: Rect; border: number; held?: 0 | 1 };
}

const SELECT = '#1863DC';

/* ------------------------------------------------------------ primitives */

export function roundRectPath(ctx: CanvasRenderingContext2D, r: Rect, radius: number): void {
  ctx.beginPath();
  roundRectSubpath(ctx, r, radius);
}

/** A rounded rectangle added to the current path (no beginPath). */
export function roundRectSubpath(ctx: CanvasRenderingContext2D, r: Rect, radius: number): void {
  const rad = Math.max(0, Math.min(radius, r.w / 2, r.h / 2));
  if (typeof ctx.roundRect === 'function') {
    ctx.roundRect(r.x, r.y, r.w, r.h, rad);
    return;
  }
  ctx.moveTo(r.x + rad, r.y);
  ctx.arcTo(r.x + r.w, r.y, r.x + r.w, r.y + r.h, rad);
  ctx.arcTo(r.x + r.w, r.y + r.h, r.x, r.y + r.h, rad);
  ctx.arcTo(r.x, r.y + r.h, r.x, r.y, rad);
  ctx.arcTo(r.x, r.y, r.x + r.w, r.y, rad);
  ctx.closePath();
}

function polyPath(ctx: CanvasRenderingContext2D, pts: number[], close: boolean, begin = true): void {
  if (begin) ctx.beginPath();
  if (pts.length < 2) return;
  ctx.moveTo(pts[0]!, pts[1]!);
  for (let i = 2; i < pts.length; i += 2) ctx.lineTo(pts[i]!, pts[i + 1]!);
  if (close) ctx.closePath();
}

function shapePath(ctx: CanvasRenderingContext2D, s: Shape, m: Metrics, begin = true): void {
  if (s.k === 'rect') {
    if (begin) ctx.beginPath();
    roundRectSubpath(ctx, shapeBounds(s), m.areaRadius);
  } else if (s.k === 'poly') polyPath(ctx, s.pts, true, begin);
  else if (s.k === 'path') pathOn(ctx, s.pts, s.h, begin);
  else polyPath(ctx, s.pts, false, begin);
}

/** The outline a selected mark glows along: its own edge (a legacy band's outline). */
function shapeOutline(ctx: CanvasRenderingContext2D, s: Shape, m: Metrics): void {
  if (s.k === 'stroke') polyPath(ctx, bandOutlineOf(s), true, false);
  else if (s.k === 'poly') roundedPolygonPath(ctx, s.pts, m.cornerRadius, false);
  else shapePath(ctx, s, m, false);
}
function bandOutlineOf(s: Extract<Shape, { k: 'stroke' }>): number[] {
  return shapeToPolygon(s);
}

/* --------------------------------------------------------------- shapes */

let scratch: HTMLCanvasElement | null = null;
function getScratch(w: number, h: number): CanvasRenderingContext2D {
  if (!scratch) scratch = document.createElement('canvas');
  if (scratch.width < w || scratch.height < h) {
    scratch.width = Math.max(scratch.width, Math.ceil(w));
    scratch.height = Math.max(scratch.height, Math.ceil(h));
  }
  const c = scratch.getContext('2d')!;
  c.setTransform(1, 0, 0, 1, 0, 0);
  c.globalCompositeOperation = 'source-over';
  c.globalAlpha = 1;
  c.clearRect(0, 0, scratch.width, scratch.height);
  return c;
}
/** Release the scratch canvas memory (WebKit counts total canvas memory). */
export function releaseScratch(): void {
  for (const c of [scratch, glowCanvas, liftCanvas, casingCanvas]) {
    if (!c) continue;
    c.width = 1;
    c.height = 1;
  }
  scratch = null;
  glowCanvas = null;
  liftCanvas = null;
  casingCanvas = null;
}

/**
 * A translucent band with a solid edge along a polyline. Rendered through a
 * scratch canvas so the translucent body never tints the solid edge, then
 * composited with `multiply` so black linework stays black.
 */
export function paintBand(ctx: CanvasRenderingContext2D, pts: number[], width: number, color: string, m: Metrics, alpha = BAND_ALPHA, bandCuts: readonly Shape[] = []): void {
  if (pts.length < 2) return;
  const edge = m.bandEdge;
  const pad = width / 2 + edge + 2;
  const b = polyBounds(pts);
  const box = { x: Math.floor(b.x - pad), y: Math.floor(b.y - pad), w: Math.ceil(b.w + 2 * pad), h: Math.ceil(b.h + 2 * pad) };
  const t = ctx.getTransform();
  let s = Math.max(Math.hypot(t.a, t.b), 0.05);
  const maxSide = 4096;
  if (box.w * s > maxSide || box.h * s > maxSide) s = Math.min(maxSide / box.w, maxSide / box.h);
  const sc = getScratch(box.w * s, box.h * s);
  sc.setTransform(s, 0, 0, s, -box.x * s, -box.y * s);
  sc.lineCap = 'butt'; // flat ends (a highlighter stroke), rounded corners
  sc.lineJoin = 'round';
  polyPath(sc, pts, false);
  if (pts.length === 2 && pts[0] === pts[2] && pts[1] === pts[3]) sc.lineTo(pts[0]! + 0.01, pts[1]!);
  sc.strokeStyle = color;
  sc.lineWidth = width + 2 * edge;
  sc.stroke();
  sc.globalCompositeOperation = 'destination-out';
  sc.lineWidth = width;
  sc.stroke();
  sc.globalCompositeOperation = 'source-over';
  sc.globalAlpha = alpha;
  sc.lineWidth = width + 0.6;
  sc.stroke();
  if (bandCuts.length) {
    // the colour band stays out of earlier placements' bodies (the seam is one band wide); the body is drawn back where it overlapped them
    sc.globalAlpha = 1;
    sc.globalCompositeOperation = 'destination-out';
    sc.beginPath();
    for (const c of bandCuts) bodyPath(sc, c, m, false);
    sc.fill();
    sc.globalCompositeOperation = 'source-over';
  }
  ctx.save();
  ctx.globalCompositeOperation = 'multiply';
  ctx.drawImage(scratch!, 0, 0, box.w * s, box.h * s, box.x, box.y, box.w, box.h);
  ctx.restore();
}

/** The body of a shape as one subpath (a legacy band's outline), for clipping. */
function bodyPath(ctx: CanvasRenderingContext2D, s: Shape, m: Metrics, begin: boolean): void {
  if (s.k === 'stroke') polyPath(ctx, bandOutlineOf(s), true, begin);
  else if (s.k === 'poly') roundedPolygonPath(ctx, s.pts, m.cornerRadius, begin);
  else shapePath(ctx, s, m, begin);
}

/**
 * A translucent body with a colour band along its border, both multiplied over
 * the plan. The body reaches the border (which runs just inside the outer edge
 * of the plan's black line); the band lies entirely beyond it, `strokeWidth`
 * wide, so the black line stays black and the colour hugs its outside. The band
 * is not painted inside `bandCuts` (the bodies of earlier placements' marks):
 * where two placements abut, the seam is one band wide, never two.
 */
function paintFilledShape(ctx: CanvasRenderingContext2D, s: Shape, fill: string, stroke: string, alpha: number, m: Metrics, strokeWidth: number, edgeAlpha = EDGE_ALPHA, bandCuts: readonly Shape[] = [], cutRegions: readonly number[][] = []): void {
  const path = (begin: boolean) => {
    if (s.k === 'poly') roundedPolygonPath(ctx, s.pts, m.cornerRadius, begin);
    else shapePath(ctx, s, m, begin);
  };
  // what is cut out of it (cuts.ts: the regions do not overlap one another)
  const holes = cutRegions.length ? cutRegions : null;
  const b = shapeBounds(s);
  const pad = 2 * strokeWidth + 2;
  const around = () => ctx.rect(b.x - pad, b.y - pad, b.w + 2 * pad, b.h + 2 * pad);
  const holePaths = (begin: boolean) => {
    if (begin) ctx.beginPath();
    for (const hl of holes ?? []) polyPath(ctx, hl, true, false);
  };
  ctx.save();
  // what is cut out of it is not the mark: neither its body nor its border's band lies there
  if (holes) {
    ctx.beginPath();
    around();
    holePaths(false);
    ctx.clip('evenodd');
  }
  path(true);
  ctx.globalCompositeOperation = 'multiply';
  ctx.globalAlpha = alpha;
  ctx.fillStyle = fill;
  ctx.fill();
  // only the outer half of a stroke twice the band's width shows: clip to everything outside the shape
  ctx.beginPath();
  around();
  path(false);
  ctx.clip('evenodd');
  if (bandCuts.length) {
    ctx.beginPath();
    around();
    for (const c of bandCuts) bodyPath(ctx, c, m, false);
    ctx.clip('evenodd');
  }
  path(true);
  ctx.globalAlpha = edgeAlpha;
  ctx.lineJoin = 'round';
  ctx.strokeStyle = stroke;
  ctx.lineWidth = 2 * strokeWidth;
  ctx.stroke();
  ctx.restore();
  if (!holes) return;
  // each region cut out has the band of its own edge, inside it (where the mark was)
  ctx.save();
  path(true);
  ctx.clip();
  holePaths(true);
  ctx.clip();
  holePaths(true);
  ctx.globalCompositeOperation = 'multiply';
  ctx.globalAlpha = edgeAlpha;
  ctx.lineJoin = 'round';
  ctx.strokeStyle = stroke;
  ctx.lineWidth = 2 * strokeWidth;
  ctx.stroke();
  ctx.restore();
}

export function drawArea(ctx: CanvasRenderingContext2D, a: WorkArea, m: Metrics, cuts: readonly number[][] = []): void {
  if (a.status === 'fail') paintFilledShape(ctx, a.shape, FAIL_AREA_FILL, FAIL, AREA_FILL_ALPHA + 0.08, m, m.areaBorder, EDGE_ALPHA, [], cuts);
  else {
    const c = areaColors(a.color);
    paintFilledShape(ctx, a.shape, c.fill, c.stroke, AREA_FILL_ALPHA, m, m.areaBorder, EDGE_ALPHA, [], cuts);
  }
}

export function drawHighlight(ctx: CanvasRenderingContext2D, hl: { material: string; shape: Shape }, m: Metrics, bandCuts: readonly Shape[] = [], cuts: readonly number[][] = []): void {
  const color = materialColor(hl.material);
  if (hl.shape.k === 'stroke') paintBand(ctx, hl.shape.pts, hl.shape.width, color, m, BAND_ALPHA, bandCuts);
  else paintFilledShape(ctx, hl.shape, color, color, BAND_ALPHA, m, m.bandEdge, EDGE_ALPHA, bandCuts, cuts);
}

/** A band's border polygon while it is being drawn: same look as the committed shape. */
export function paintOutline(ctx: CanvasRenderingContext2D, pts: number[], color: string, m: Metrics, alpha = BAND_ALPHA): void {
  if (pts.length < 6) return;
  paintFilledShape(ctx, { k: 'poly', pts }, color, color, alpha, m, m.bandEdge);
}

/**
 * Several border polygons drawn as one mark (their union fills once; overlaps
 * do not darken), the colour band outside the union only. Built on the scratch
 * canvas so the band can be cut back exactly to the union's border. `cuts` are
 * polygons the mark yields to (the marks of other placements): none of its body
 * is painted inside them; its band keeps out of `bandCuts` (the bodies of lower
 * placements) and runs across the seam into the others, exactly as a committed
 * mark is drawn.
 */
export function paintOutlines(ctx: CanvasRenderingContext2D, polys: number[][], color: string, m: Metrics, alpha = BAND_ALPHA, edgeAlpha = 1, cuts: readonly number[][] = [], bandCuts: readonly number[][] = cuts): void {
  const shown = polys.filter((p) => p.length >= 6);
  if (!shown.length) return;
  const edge = m.bandEdge;
  const pad = 2 * edge + 2;
  const b = polyBounds(shown.flat());
  const box = { x: Math.floor(b.x - pad), y: Math.floor(b.y - pad), w: Math.ceil(b.w + 2 * pad), h: Math.ceil(b.h + 2 * pad) };
  const t = ctx.getTransform();
  let s = Math.max(Math.hypot(t.a, t.b), 0.05);
  const maxSide = 4096;
  if (box.w * s > maxSide || box.h * s > maxSide) s = Math.min(maxSide / box.w, maxSide / box.h);
  const sc = getScratch(box.w * s, box.h * s);
  sc.setTransform(s, 0, 0, s, -box.x * s, -box.y * s);
  const path = () => {
    sc.beginPath();
    for (const p of shown) roundedPolygonPath(sc, p, m.cornerRadius, false);
  };
  const erase = (polysToErase: readonly number[][]) => {
    if (!polysToErase.length) return;
    sc.globalAlpha = 1;
    sc.globalCompositeOperation = 'destination-out';
    sc.beginPath();
    for (const c of polysToErase) if (c.length >= 6) polyPath(sc, c, true, false);
    sc.fill();
    sc.globalCompositeOperation = 'source-over';
  };
  // the body, kept out of the cuts
  sc.globalAlpha = alpha;
  sc.fillStyle = color;
  path();
  sc.fill();
  erase(cuts);
  // the band: a stroke twice as wide, clipped to outside the union (so only the outer half
  // shows and the body underneath is untouched), kept out of the band cuts
  sc.save();
  sc.beginPath();
  sc.rect(box.x - 1, box.y - 1, box.w + 2, box.h + 2);
  for (const p of shown) roundedPolygonPath(sc, p, m.cornerRadius, false);
  sc.clip('evenodd');
  path();
  sc.lineJoin = 'round';
  sc.strokeStyle = color;
  sc.lineWidth = 2 * edge;
  sc.globalAlpha = EDGE_ALPHA * edgeAlpha;
  sc.stroke();
  erase(bandCuts);
  sc.restore();
  ctx.save();
  ctx.globalCompositeOperation = 'multiply';
  ctx.drawImage(scratch!, 0, 0, box.w * s, box.h * s, box.x, box.y, box.w, box.h);
  ctx.restore();
}

/** Cut polygons out of what the layer holds (a live shape yielding to the marks of other placements). */
export function erasePolygons(ctx: CanvasRenderingContext2D, cuts: readonly number[][]): void {
  if (!cuts.length) return;
  ctx.save();
  ctx.globalCompositeOperation = 'destination-out';
  ctx.globalAlpha = 1;
  ctx.fillStyle = '#000';
  ctx.beginPath();
  for (const c of cuts) if (c.length >= 6) polyPath(ctx, c, true, false);
  ctx.fill();
  ctx.restore();
}

/** The finger's recent route as a soft glowing trail, painted while the plan is being read. */
export function drawGlowTrail(ctx: CanvasRenderingContext2D, pts: number[], color: string, viewScale: number, alpha = 1): void {
  if (pts.length < 2) return;
  ctx.save();
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.strokeStyle = color;
  const path = () => {
    ctx.beginPath();
    ctx.moveTo(pts[0]!, pts[1]!);
    for (let i = 2; i < pts.length; i += 2) ctx.lineTo(pts[i]!, pts[i + 1]!);
    if (pts.length === 2) ctx.lineTo(pts[0]! + 0.01, pts[1]!);
  };
  for (const [w, a] of [[34, 0.07], [20, 0.12], [9, 0.28]] as const) {
    ctx.globalAlpha = a * alpha;
    ctx.lineWidth = w / viewScale;
    path();
    ctx.stroke();
  }
  ctx.restore();
  drawGlowTip(ctx, { x: pts[pts.length - 2]!, y: pts[pts.length - 1]! }, color, viewScale, 0.5 * alpha);
}

/** A soft glow under the fingertip. */
export function drawGlowTip(ctx: CanvasRenderingContext2D, p: { x: number; y: number }, color: string, viewScale: number, alpha = 0.35): void {
  const r = 22 / viewScale;
  const g = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, r);
  g.addColorStop(0, color);
  g.addColorStop(1, 'rgba(0,0,0,0)');
  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.fillStyle = g;
  ctx.beginPath();
  ctx.arc(p.x, p.y, r, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

/* ----------------------------------------------------------------- lift */

let liftCanvas: HTMLCanvasElement | null = null;

/** How far beyond a mark's colour band the plan is lifted too: a hair of paper round the band on a shaded plan. */
export function liftBeyond(m: Metrics): number {
  return m.leaderHalo + 0.3 * m.u;
}

/** What is lifted: a mark's shape, or a bare outline (a mark being drawn), with the width of its colour band. */
export interface LiftPart {
  shape?: Shape;
  poly?: number[];
  band: number;
  /** Regions cut out of it (cuts.ts). */
  cuts?: readonly number[][];
}

/**
 * The plan lifted (lift.ts) under marks — their bodies, their colour bands and a hair beyond —
 * painted over the plan before the marks themselves, so that on a shaded plan each reads as on
 * white paper. `keepOut`: marks already drawn with their own lift, left as they are (a mark being
 * drawn on the live layer over them). Only what is on the canvas is painted.
 */
export function paintLift(ctx: CanvasRenderingContext2D, parts: readonly LiftPart[], lifted: CanvasImageSource, w: number, h: number, m: Metrics, keepOut: readonly LiftPart[] = []): void {
  if (!parts.length) return;
  const beyond = liftBeyond(m);
  let x0 = Infinity,
    y0 = Infinity,
    x1 = -Infinity,
    y1 = -Infinity;
  for (const p of parts) {
    const b = p.shape ? shapeBounds(p.shape) : polyBounds(p.poly ?? []);
    const pad = p.band + beyond + 1;
    x0 = Math.min(x0, b.x - pad);
    y0 = Math.min(y0, b.y - pad);
    x1 = Math.max(x1, b.x + b.w + pad);
    y1 = Math.max(y1, b.y + b.h + pad);
  }
  // only the part of it on the plan and on the canvas
  const t = ctx.getTransform();
  const s0 = Math.max(Math.hypot(t.a, t.b), 1e-6);
  x0 = Math.max(x0, 0, -t.e / s0 - 1);
  y0 = Math.max(y0, 0, -t.f / s0 - 1);
  x1 = Math.min(x1, w, (ctx.canvas.width - t.e) / s0 + 1);
  y1 = Math.min(y1, h, (ctx.canvas.height - t.f) / s0 + 1);
  if (!(x1 > x0 && y1 > y0)) return;
  const box = { x: Math.floor(x0), y: Math.floor(y0), w: Math.ceil(x1 - Math.floor(x0)), h: Math.ceil(y1 - Math.floor(y0)) };
  let s = s0;
  const maxSide = 4096;
  if (box.w * s > maxSide || box.h * s > maxSide) s = Math.min(maxSide / box.w, maxSide / box.h);
  const cw = Math.ceil(box.w * s),
    ch = Math.ceil(box.h * s);
  if (!liftCanvas) liftCanvas = document.createElement('canvas');
  if (liftCanvas.width < cw || liftCanvas.height < ch) {
    liftCanvas.width = Math.max(liftCanvas.width, cw);
    liftCanvas.height = Math.max(liftCanvas.height, ch);
  }
  const sc = liftCanvas.getContext('2d')!;
  sc.setTransform(1, 0, 0, 1, 0, 0);
  sc.globalCompositeOperation = 'source-over';
  sc.globalAlpha = 1;
  sc.clearRect(0, 0, cw + 2, ch + 2);
  sc.setTransform(s, 0, 0, s, -box.x * s, -box.y * s);
  const region = (list: readonly LiftPart[], holes: boolean) => {
    sc.fillStyle = '#000';
    sc.strokeStyle = '#000';
    sc.lineJoin = 'round';
    for (const p of list) {
      sc.beginPath();
      if (p.shape) bodyPath(sc, p.shape, m, false);
      else if (p.poly && p.poly.length >= 6) polyPath(sc, p.poly, true, false);
      else continue;
      sc.fill();
      sc.lineWidth = 2 * (p.band + beyond);
      sc.stroke();
    }
    if (!holes) return;
    // a region cut out is the plan as it is, but for the band round its edge (and the hair beyond)
    for (const p of list) {
      if (!p.cuts?.length) continue;
      sc.beginPath();
      for (const hl of p.cuts) polyPath(sc, hl, true, false);
      sc.globalCompositeOperation = 'destination-out';
      sc.fill();
      sc.globalCompositeOperation = 'source-over';
      sc.lineWidth = 2 * (p.band + beyond);
      sc.stroke();
    }
  };
  region(parts, true);
  if (keepOut.length) {
    sc.globalCompositeOperation = 'destination-out';
    region(keepOut, false);
  }
  sc.globalCompositeOperation = 'source-in';
  sc.imageSmoothingEnabled = true;
  sc.imageSmoothingQuality = 'high';
  sc.drawImage(lifted, 0, 0, w, h);
  sc.globalCompositeOperation = 'source-over';
  ctx.save();
  ctx.globalCompositeOperation = 'source-over';
  ctx.globalAlpha = 1;
  ctx.drawImage(liftCanvas, 0, 0, cw, ch, box.x, box.y, cw / s, ch / s);
  ctx.restore();
}

/** What of a document is lifted: every work area and mark, each with its colour band (and less its cuts). */
export function liftParts(doc: MapDoc, m: Metrics): LiftPart[] {
  const out: LiftPart[] = [];
  for (const it of doc.items) {
    if (it.t !== 'area' && it.t !== 'hl') continue;
    const cuts = cutRegions(cutsOf(doc.items, it.id));
    out.push({ shape: it.shape, band: it.t === 'area' ? m.areaBorder : m.bandEdge, ...(cuts.length ? { cuts } : {}) });
  }
  return out;
}

/**
 * The plan lifted under a document's marks (see `paintLift`), from the plan's own picture. False
 * when there is nothing to lift (a plan all paper and ink), no picture, or — `sync` false, as the
 * editor draws — the lifted plan is not made yet: the marks are then drawn straight on the plan.
 */
export function drawLift(ctx: CanvasRenderingContext2D, doc: MapDoc, m: Metrics, plan: CanvasImageSource | null, sync = true): boolean {
  if (!plan) return false;
  const lifted = liftedPlan(plan, doc.w, doc.h, sync);
  if (!lifted) return false;
  paintLift(ctx, liftParts(doc, m), lifted, doc.w, doc.h, m);
  return true;
}

/** The plan lifted under a mark being drawn — only there, the marks already drawn keeping their own (the live layer lies over them). */
export function liftUnder(ctx: CanvasRenderingContext2D, doc: MapDoc, m: Metrics, plan: CanvasImageSource | null, parts: readonly LiftPart[], sync = true): void {
  if (!plan || !parts.length) return;
  const lifted = liftedPlan(plan, doc.w, doc.h, sync);
  if (lifted) paintLift(ctx, parts, lifted, doc.w, doc.h, m, liftParts(doc, m));
}

/**
 * Areas and highlight bands — everything that sits directly on the plan (`except` is
 * left to the caller). Where placements abut, the earlier placement's band runs across
 * the seam and the later one's stops at it: a mark's band keeps out of the bodies of
 * marks of lower placements.
 */
export function drawShapes(ctx: CanvasRenderingContext2D, doc: MapDoc, m: Metrics, except: string | null = null): void {
  const marks = doc.items.filter((i): i is Highlight => i.t === 'hl'); // the excepted mark is on the map too: bands keep out of it
  paintMarkHalos(ctx, doc, m);
  for (const it of doc.items) {
    if (it.id === except) continue;
    if (it.t === 'area') drawArea(ctx, it, m, cutRegions(cutsOf(doc.items, it.id)));
    else if (it.t === 'hl') {
      const own = placementOf(it);
      const earlier = marks.filter((o) => placementOf(o) < own).map((o) => o.shape);
      drawHighlight(ctx, it, m, earlier, cutRegions(cutsOf(doc.items, it.id)));
    }
  }
}

/**
 * A thin white halo just beyond every mark's colour band, so a mark stands clear of whatever the
 * plan has round it — dense linework, hatching, shading — however busy. It is the plan that gives
 * way, never a mark: the halos together are kept off every mark's body (a mark's band over its
 * neighbour's halo shows as its own colour, on white).
 */
function paintMarkHalos(ctx: CanvasRenderingContext2D, doc: MapDoc, m: Metrics): void {
  const marks = doc.items.filter((i): i is WorkArea | Highlight => i.t === 'area' || i.t === 'hl');
  if (!marks.length) return;
  const reach = (it: WorkArea | Highlight) => (it.t === 'area' ? m.areaBorder : m.bandEdge) + m.markHalo;
  let x0 = Infinity,
    y0 = Infinity,
    x1 = -Infinity,
    y1 = -Infinity;
  for (const it of marks) {
    const b = shapeBounds(it.shape);
    const r = reach(it) + 1;
    x0 = Math.min(x0, b.x - r);
    y0 = Math.min(y0, b.y - r);
    x1 = Math.max(x1, b.x + b.w + r);
    y1 = Math.max(y1, b.y + b.h + r);
  }
  // only what is on the canvas
  const t = ctx.getTransform();
  const s0 = Math.max(Math.hypot(t.a, t.b), 1e-6);
  x0 = Math.max(x0, -t.e / s0 - 1);
  y0 = Math.max(y0, -t.f / s0 - 1);
  x1 = Math.min(x1, (ctx.canvas.width - t.e) / s0 + 1);
  y1 = Math.min(y1, (ctx.canvas.height - t.f) / s0 + 1);
  if (!(x1 > x0 && y1 > y0)) return;
  const box = { x: Math.floor(x0), y: Math.floor(y0), w: Math.ceil(x1 - Math.floor(x0)), h: Math.ceil(y1 - Math.floor(y0)) };
  let s = s0;
  const maxSide = 4096;
  if (box.w * s > maxSide || box.h * s > maxSide) s = Math.min(maxSide / box.w, maxSide / box.h);
  const sc = getScratch(box.w * s, box.h * s);
  sc.setTransform(s, 0, 0, s, -box.x * s, -box.y * s);
  sc.lineJoin = 'round';
  sc.strokeStyle = PAPER;
  for (const it of marks) {
    bodyPath(sc, it.shape, m, true);
    sc.lineWidth = 2 * reach(it);
    sc.stroke();
  }
  sc.globalCompositeOperation = 'destination-out';
  for (const it of marks) {
    bodyPath(sc, it.shape, m, true);
    sc.fill();
  }
  sc.globalCompositeOperation = 'source-over';
  ctx.save();
  ctx.globalCompositeOperation = 'source-over';
  ctx.globalAlpha = 1;
  ctx.drawImage(scratch!, 0, 0, box.w * s, box.h * s, box.x, box.y, box.w, box.h);
  ctx.restore();
}

/* -------------------------------------------------------------- leaders */

export function leaderGeometry(rect: Rect, target: { x: number; y: number }, m: Metrics, tipInset: number): LeaderGeom {
  return leaderGeom(rect, target, m.pillRadius, m.labelStub, tipInset);
}

function arrowheadPath(ctx: CanvasRenderingContext2D, tip: { x: number; y: number }, dir: { x: number; y: number }, m: Metrics): void {
  const back = add(tip, mul(dir, -m.arrowLen));
  const n = { x: -dir.y, y: dir.x };
  const l = add(back, mul(n, m.arrowWidth / 2));
  const r = add(back, mul(n, -m.arrowWidth / 2));
  ctx.beginPath();
  ctx.moveTo(tip.x, tip.y);
  ctx.lineTo(l.x, l.y);
  ctx.lineTo(r.x, r.y);
  ctx.closePath();
}

/**
 * A note's leader with its arrowhead, in one piece: the line running into the head and the head
 * over it — no notch where they meet. (Its white casing is on the casing layer: `casings`.)
 */
function paintLeader(ctx: CanvasRenderingContext2D, g: LeaderGeom | LeaderRoute, color: string, m: Metrics): void {
  paintLeaders(ctx, [{ ...g, radii: g.pts.map(() => 0) }], color, m);
}

/** A leader's line as a path, each corner eased into an arc of its radius (see `easedRadii`: as far as stays clear of everything). */
function easedPath(ctx: CanvasRenderingContext2D, pts: readonly { x: number; y: number }[], radii: readonly number[]): void {
  ctx.beginPath();
  const n = pts.length;
  if (!n) return;
  ctx.moveTo(pts[0]!.x, pts[0]!.y);
  for (let i = 1; i + 1 < n; i++) {
    const r = radii[i] ?? 0;
    if (r > 0.5) ctx.arcTo(pts[i]!.x, pts[i]!.y, pts[i + 1]!.x, pts[i + 1]!.y, r);
    else ctx.lineTo(pts[i]!.x, pts[i]!.y);
  }
  ctx.lineTo(pts[n - 1]!.x, pts[n - 1]!.y);
}

/** What painting a routed leader needs: its line, its tip and the way the tip points, and its eased corners. */
type Routed = Pick<LeaderRoute, 'pts' | 'tip' | 'dir'> & { radii?: readonly number[]; target?: { x: number; y: number } };
/**
 * A leader's line as drawn: it ends inside its head — or, a plain line to a test's dot, runs on
 * into the dot itself (`target`), under it, so the dot's white ring opens for the line and the
 * line's casing joins the dot's — its corners eased.
 */
function leaderLine(g: Routed, m: Metrics, head: boolean): { pts: { x: number; y: number }[]; radii: readonly number[] } {
  const pts = g.pts.slice();
  if (head && pts.length >= 2) pts[pts.length - 1] = add(g.tip, mul(g.dir, -0.45 * m.arrowLen)); // the line ends inside the head
  else if (!head && g.target && pts.length >= 2) pts[pts.length - 1] = g.target;
  return { pts, radii: g.radii ?? easedRadii(g.pts, 1.5 * m.fontCallout, HEAD_RUN * m.arrowLen) };
}

/**
 * The white casing of leaders — of each line and, with `heads`, round each arrowhead too (the
 * inspectors' own markups: the arrows stand off the plan's line work on a thin white border). It is
 * painted on the casing layer (`casings`), which is cut back from every mark: it never bites in.
 */
function caseLeaders(ctx: CanvasRenderingContext2D, routes: readonly Routed[], m: Metrics, heads: boolean): void {
  ctx.save();
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.strokeStyle = PAPER;
  ctx.fillStyle = PAPER;
  ctx.lineWidth = m.leader + 2 * m.leaderHalo;
  for (const g of routes) {
    const l = leaderLine(g, m, heads);
    easedPath(ctx, l.pts, l.radii);
    ctx.stroke();
  }
  if (heads) {
    ctx.lineWidth = 2 * m.leaderHalo;
    for (const g of routes) {
      arrowheadPath(ctx, g.tip, g.dir, m);
      ctx.fill();
      ctx.stroke();
    }
  }
  ctx.restore();
}

/**
 * A callout's leaders, which share their root and fan out: every line, then the heads (their
 * casings, all of them, lie under everything on the casing layer — so no arrow's casing bites a
 * notch into another where they part).
 */
function paintLeaders(ctx: CanvasRenderingContext2D, routes: readonly Routed[], color: string, m: Metrics): void {
  ctx.save();
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.strokeStyle = color;
  ctx.lineWidth = m.leader;
  for (const g of routes) {
    const l = leaderLine(g, m, true);
    easedPath(ctx, l.pts, l.radii);
    ctx.stroke();
  }
  ctx.fillStyle = color;
  for (const g of routes) {
    arrowheadPath(ctx, g.tip, g.dir, m);
    ctx.fill();
  }
  ctx.restore();
}

/** A test's leader: a plain line into its dot (no head; the dot is drawn over its end), its corners eased as routed. */
function strokeRouted(ctx: CanvasRenderingContext2D, g: SoilsLeader, color: string, m: Metrics): void {
  ctx.save();
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  const l = leaderLine(g, m, false);
  easedPath(ctx, l.pts, l.radii);
  ctx.strokeStyle = color;
  ctx.lineWidth = m.leader;
  ctx.stroke();
  ctx.restore();
}

/** The white casing round a box — a callout's, a label's, a note's: a thin border of paper beyond its own. */
function caseBox(ctx: CanvasRenderingContext2D, r: Rect, radius: number, border: number, m: Metrics): void {
  roundRectPath(ctx, r, radius);
  ctx.strokeStyle = PAPER;
  ctx.lineJoin = 'round';
  ctx.lineWidth = border + 2 * m.leaderHalo;
  ctx.stroke();
}

let casingCanvas: HTMLCanvasElement | null = null;
/**
 * The casing layer: every white casing (drawn by `draw`) on a canvas of its own, cut back from
 * every mark's colour band (and a hair either side of it), and then laid on the plan under the
 * leaders and boxes. However near a leader, an arrowhead or a box comes to a mark's border, no
 * white ever bites into the band. Over a mark's translucent body the casings stay: a test's leader
 * keeps its white border all the way into the dot, as the dot keeps its ring there.
 */
function casings(ctx: CanvasRenderingContext2D, doc: MapDoc, m: Metrics, within: Rect | null, draw: (c: CanvasRenderingContext2D) => void): void {
  if (!within) return;
  // only where there are casings (`within`, image px) and on the canvas, in device px
  const t = ctx.getTransform();
  const pad = m.leader + 2 * m.leaderHalo + m.calloutBorder;
  const x0 = Math.max(0, Math.floor((within.x - pad) * t.a + t.e)),
    y0 = Math.max(0, Math.floor((within.y - pad) * t.d + t.f));
  const x1 = Math.min(ctx.canvas.width, Math.ceil((within.x + within.w + pad) * t.a + t.e)),
    y1 = Math.min(ctx.canvas.height, Math.ceil((within.y + within.h + pad) * t.d + t.f));
  const W = x1 - x0,
    H = y1 - y0;
  if (W < 1 || H < 1) return;
  if (!casingCanvas) casingCanvas = document.createElement('canvas');
  if (casingCanvas.width < W || casingCanvas.height < H) {
    casingCanvas.width = Math.max(casingCanvas.width, W);
    casingCanvas.height = Math.max(casingCanvas.height, H);
  }
  const c = casingCanvas.getContext('2d')!;
  c.setTransform(1, 0, 0, 1, 0, 0);
  c.globalCompositeOperation = 'source-over';
  c.globalAlpha = 1;
  c.clearRect(0, 0, W, H);
  c.setTransform(t.a, t.b, t.c, t.d, t.e - x0, t.f - y0);
  draw(c);
  c.globalAlpha = 1;
  c.globalCompositeOperation = 'destination-out';
  c.fillStyle = '#000';
  c.strokeStyle = '#000';
  c.lineJoin = 'round';
  for (const it of doc.items) {
    if (it.t !== 'area' && it.t !== 'hl') continue;
    const band = it.t === 'area' ? m.areaBorder : m.bandEdge;
    bodyPath(c, it.shape, m, true);
    c.lineWidth = 2 * band + 0.6 * m.u;
    c.stroke();
  }
  c.globalCompositeOperation = 'source-over';
  ctx.save();
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.globalAlpha = 1;
  ctx.globalCompositeOperation = 'source-over';
  ctx.drawImage(casingCanvas, 0, 0, W, H, x0, y0, W, H);
  ctx.restore();
}

/** What a set of boxes and leaders covers (image px), or null when there is none. */
function coverOf(rects: readonly Rect[], lines: readonly (readonly { x: number; y: number }[])[]): Rect | null {
  let x0 = Infinity,
    y0 = Infinity,
    x1 = -Infinity,
    y1 = -Infinity;
  const take = (x: number, y: number) => {
    x0 = Math.min(x0, x);
    y0 = Math.min(y0, y);
    x1 = Math.max(x1, x);
    y1 = Math.max(y1, y);
  };
  for (const r of rects) {
    take(r.x, r.y);
    take(r.x + r.w, r.y + r.h);
  }
  for (const l of lines) for (const p of l) take(p.x, p.y);
  return x1 >= x0 ? { x: x0, y: y0, w: x1 - x0, h: y1 - y0 } : null;
}

/* ---------------------------------------------------------------- pills */

interface PillStyle {
  fill: string;
  fillAlpha: number;
  border: string;
  borderWidth: number;
  text: string;
  textAlpha?: number;
  accent: string | null;
  radius: number;
}

function drawPill(ctx: CanvasRenderingContext2D, box: LabelBox, st: PillStyle, m: Metrics): void {
  const r = box.rect;
  ctx.save();
  roundRectPath(ctx, r, st.radius);
  ctx.globalAlpha = st.fillAlpha;
  ctx.fillStyle = st.fill;
  ctx.fill();
  ctx.globalAlpha = 1;
  if (st.accent) {
    ctx.save();
    ctx.clip();
    ctx.fillStyle = st.accent;
    ctx.fillRect(r.x, r.y, m.accentBar, r.h);
    ctx.restore();
  }
  roundRectPath(ctx, r, st.radius);
  ctx.strokeStyle = st.border;
  ctx.lineWidth = st.borderWidth;
  ctx.stroke();
  ctx.globalAlpha = st.textAlpha ?? 1;
  ctx.fillStyle = st.text;
  ctx.textBaseline = 'alphabetic';
  ctx.textAlign = 'left';
  const k = box.k ?? 1;
  const x = r.x + m.pillPadX * k + (st.accent ? m.accentBar : 0);
  const lh = box.size * m.lineHeight;
  // its words as laid out — wrapped, aligned, each stretch in its own look
  if (box.rich) {
    drawRich(ctx, box.rich, x, r.y + m.pillPadY * k, lh, r.x + r.w - m.pillPadX * k - x, box.align ?? 'left', st.text);
    ctx.restore();
    return;
  }
  box.lines.forEach((line, i) => {
    ctx.font = fontString(box.weights[i] ?? 600, box.size);
    const y = r.y + m.pillPadY + lh * i + lh / 2 + box.size * 0.36;
    ctx.fillText(line, x, y);
  });
  ctx.restore();
}

/** How the selection glow is drawn this frame: it blooms in when something is selected, then rests. */
export interface GlowState {
  /** 0…1: how much of it shows. */
  alpha: number;
  /** Multiplies the halo's reach (it opens out to 1 as it comes up). */
  grow: number;
}
export const GLOW_REST: GlowState = { alpha: 1, grow: 1 };

/**
 * How heavy the selection ring is on screen (CSS px) with the map's line weight `ink` there (a
 * line's width, 2u, × the zoom). The ring is the editor's pointer, never ink — it is not in an
 * export — so it is sized on screen, not on the plan; but a pointer that never changes looks a
 * hairline beside a border zoomed far in and a blot round a badge zoomed far out. So:
 *  - it is always seen: never under 2 px (the least a focus ring may be, WCAG 2.4.13), or on a busy
 *    plan it reads as one more of the plan's own lines;
 *  - it belongs to what it rings: its weight is the geometric mean of the thing's own line and a
 *    2.5 px line of the interface — halfway between ink and chrome — so it grows as the square root
 *    of the zoom (half as fast as the map: each doubling of the zoom makes it 41 % heavier): close
 *    in a little lighter than the thing's border, never a hairline beside it; far out a little
 *    heavier, so a small thing is still plainly the one chosen;
 *  - it never hides the plan round what is selected, which is what the inspector is looking at:
 *    never over 4 px;
 *  - it changes smoothly under a pinch, never in steps;
 *  - its soft halo is in proportion to it (4 lines' reach): tight round small things far out, where
 *    neighbours are close, fuller close up.
 * The thing in the hand is ringed a third heavier.
 */
export function ringWeight(ink: number, strong = false): { line: number; reach: number } {
  const line = Math.min(4, Math.max(2, Math.sqrt(2.5 * ink))) * (strong ? 1.3 : 1);
  return { line, reach: 4 * line };
}

let glowCanvas: HTMLCanvasElement | null = null;
/**
 * Selection: one colour, quiet — a crisp line lying against the thing's outermost white edge
 * (`offset` beyond `subpath`, image units: its colour band or border and the white casing beyond
 * it), with no plan showing between them, and a soft halo falling away outside the line. Nothing is
 * drawn on the thing or its white: the glow is painted on a canvas of its own and everything within
 * the white's outer edge is cut out of it — all but the half device pixel the white's edge is
 * smoothed over, which the line takes, so no seam of the plan shows between them. The line and the
 * halo are sized on screen (`ringWeight`). `subpath` adds the closed outline to the current path
 * without beginPath. The context's alpha carries over (a fading flash). Never in an export: only
 * the editor draws it.
 */
function glowOutline(ctx: CanvasRenderingContext2D, subpath: (c: CanvasRenderingContext2D) => void, bounds: Rect, offset: number, viewScale: number, m: Metrics, glow: GlowState = GLOW_REST, strong = false): void {
  const px = 1 / viewScale; // one screen px, in image units
  const weight = ringWeight(m.leader * viewScale, strong);
  const line = weight.line; // the line, screen px (heavier for the thing in the hand)
  const reach = weight.reach * glow.grow; // the halo beyond the line, screen px
  // the part of the glow that is on the canvas, in device px
  const t = ctx.getTransform();
  const pad = offset + (line + reach + 2) * px;
  const x0 = Math.max(0, Math.floor((bounds.x - pad) * t.a + t.e)),
    y0 = Math.max(0, Math.floor((bounds.y - pad) * t.d + t.f));
  const x1 = Math.min(ctx.canvas.width, Math.ceil((bounds.x + bounds.w + pad) * t.a + t.e)),
    y1 = Math.min(ctx.canvas.height, Math.ceil((bounds.y + bounds.h + pad) * t.d + t.f));
  const w = x1 - x0,
    h = y1 - y0;
  if (w <= 0 || h <= 0 || glow.alpha <= 0) return;
  if (!glowCanvas) glowCanvas = document.createElement('canvas');
  if (glowCanvas.width < w || glowCanvas.height < h) {
    glowCanvas.width = Math.max(glowCanvas.width, w);
    glowCanvas.height = Math.max(glowCanvas.height, h);
  }
  const sc = glowCanvas.getContext('2d')!;
  // what it is drawn round, in straight lines a fifth of a device px from it (Safari draws a curve
  // stroked wider than it is tight as a band with a hole: far out, a dot's ring went altogether)
  const outline = flatten((c) => subpath(c as unknown as CanvasRenderingContext2D), 0.2 / Math.max(Math.abs(t.a), 1e-6));
  sc.setTransform(1, 0, 0, 1, 0, 0);
  sc.globalCompositeOperation = 'source-over';
  sc.globalAlpha = 1;
  sc.clearRect(0, 0, w, h);
  sc.setTransform(t.a, 0, 0, t.d, t.e - x0, t.f - y0);
  sc.lineJoin = 'round';
  sc.strokeStyle = SELECT;
  const stroke = (beyond: number, alpha: number) => {
    sc.globalAlpha = alpha;
    sc.lineWidth = 2 * (offset + beyond * px);
    flatPath(sc, outline);
    sc.stroke();
  };
  // The halo: strokes one over the other, each reaching a little less far (never more than
  // 1.25 px between two, so no step shows at any zoom), each as faint as it takes for the light
  // gathered at a distance d beyond the line to be `light(d)`: soft by the line, gone at the reach.
  const out = line;
  const light = (d: number) => (strong ? 0.36 : 0.26) * Math.pow(Math.max(0, 1 - d / reach), 2);
  const layers = Math.max(6, Math.min(20, Math.ceil(reach / 1.25)));
  const step = reach / layers;
  for (let k = layers; k >= 1; k--) stroke(out + k * step, 1 - (1 - light((k - 1) * step)) / (1 - light(k * step)));
  // the line itself, crisp
  stroke(out, 0.95);
  // nothing within the white's outer edge (but the half device px its edge is smoothed over)
  const cut = offset - 0.5 / Math.max(Math.abs(t.a), 1e-6);
  sc.globalCompositeOperation = 'destination-out';
  sc.globalAlpha = 1;
  flatPath(sc, outline);
  sc.fill();
  if (cut > 0) {
    sc.lineWidth = 2 * cut;
    sc.stroke();
  }
  ctx.save();
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.globalAlpha *= glow.alpha;
  ctx.drawImage(glowCanvas, 0, 0, w, h, x0, y0, w, h);
  ctx.restore();
}
/** The selection glow round one shape, beyond its colour band and the white beyond that (also a placement's marks lighting up when it is chosen). */
export function drawShapeGlow(ctx: CanvasRenderingContext2D, s: Shape, m: Metrics, viewScale: number, area = false, glow: GlowState = GLOW_REST): void {
  const beyond = (area ? m.areaBorder : m.bandEdge) + m.markHalo;
  glowOutline(ctx, (c) => shapeOutline(c, s, m), shapeBounds(s), beyond, viewScale, m, glow);
}
/** The selection glow round a label box whose border is stroked centred on its edge: beyond the border and its white casing. */
function glowRect(ctx: CanvasRenderingContext2D, r: Rect, radius: number, border: number, m: Metrics, viewScale: number, glow: GlowState = GLOW_REST): void {
  glowOutline(ctx, (c) => roundRectSubpath(c, r, radius), r, border / 2 + m.leaderHalo, viewScale, m, glow);
}
/**
 * The selection glow round a pinpoint: beyond its white ring — or, the pinpoints left off, beyond
 * the round white end of the leader that runs to the spot.
 */
function glowDot(ctx: CanvasRenderingContext2D, p: { x: number; y: number }, size: DotSize, m: Metrics, viewScale: number, glow: GlowState): void {
  const [r, white] = size.r > 0 ? [size.r, size.halo] : [m.leader / 2, m.leaderHalo];
  const R = r + white;
  glowOutline(ctx, (c) => c.arc(p.x, p.y, r, 0, Math.PI * 2), { x: p.x - R, y: p.y - R, w: 2 * R, h: 2 * R }, white, viewScale, m, glow);
}

/**
 * A box's grips (2026-10-28, the inspectors: "very easy to move the edges of a box"): a white
 * capsule standing on the middle of each side, on the selection ring, as tall as half the box (within
 * a fingertip's reach of 16–28 px) — the box made narrower or wider by either, its words wrapping.
 * The one in the hand is the selection's blue. Sized on screen, as the ring is.
 */
function drawGrips(ctx: CanvasRenderingContext2D, g: NonNullable<RenderOptions['grip']>, m: Metrics, vs: number, glow: GlowState): void {
  const px = 1 / vs;
  const r = g.rect;
  const ring = ringWeight(m.leader * vs);
  // on the ring's line: beyond the border and its white, half the line's weight out
  const out = g.border / 2 + m.leaderHalo + (ring.line / 2) * px;
  const h = Math.max(16, Math.min(28, r.h * vs * 0.5)) * px;
  const w = 7 * px;
  ctx.save();
  ctx.globalAlpha *= glow.alpha;
  for (const side of [0, 1] as const) {
    const cx = side === 0 ? r.x - out : r.x + r.w + out;
    const box = { x: cx - w / 2, y: r.y + r.h / 2 - h / 2, w, h };
    const held = g.held === side;
    ctx.save();
    // (a shadow is in device px, whatever the zoom)
    const dpr = Math.abs(ctx.getTransform().a) / vs || 1;
    ctx.shadowColor = 'rgba(0, 0, 0, 0.28)';
    ctx.shadowBlur = 4 * dpr;
    ctx.shadowOffsetY = 1 * dpr;
    roundRectPath(ctx, box, w / 2);
    ctx.fillStyle = held ? SELECT : PAPER;
    ctx.fill();
    ctx.restore();
    roundRectPath(ctx, box, w / 2);
    ctx.lineWidth = 1.5 * px;
    ctx.strokeStyle = SELECT;
    ctx.stroke();
  }
  ctx.restore();
}

/* -------------------------------------------------------------- markers */

/** A test's dot: one size on every map. */
function fullDot(m: Metrics): DotSize {
  return { r: m.markerR, halo: m.markerHalo };
}

/**
 * A test's dot's white ring: drawn under the leaders, so the leader into the dot opens the ring
 * (see `leaderLine`). `size`: the dot as laid out.
 */
export function drawMarkerHalo(ctx: CanvasRenderingContext2D, t: TestPoint, m: Metrics, size: DotSize = fullDot(m)): void {
  if (size.r <= 0) return;
  ctx.save();
  ctx.beginPath();
  ctx.arc(t.p.x, t.p.y, size.r + size.halo, 0, Math.PI * 2);
  ctx.fillStyle = PAPER;
  ctx.fill();
  ctx.restore();
}

/** A test's pinpoint (its white ground is `drawMarkerHalo`, under the leaders), at its size as laid out, ringed in the test's colour: passing green, failing red. */
export function drawMarker(ctx: CanvasRenderingContext2D, t: TestPoint, m: Metrics, selected: boolean, viewScale = 1, glow: GlowState = GLOW_REST, size: DotSize = fullDot(m)): void {
  // a pinpoint as every one is (2026-10-24, Ricky: all like the elevation's): a white centre ringed in the test's colour
  pinpoint(ctx, t.p, m, t.status === 'fail' ? FAIL : PASS, size);
  if (selected) glowDot(ctx, t.p, size, m, viewScale, glow);
}

/**
 * An elevation shot's white ground: drawn under the leaders, as a test's ring is, so the leader runs
 * up to its ring with its white border whole.
 */
export function drawElevHalo(ctx: CanvasRenderingContext2D, e: Elevation, size: DotSize): void {
  if (size.r <= 0) return;
  ctx.save();
  ctx.beginPath();
  ctx.arc(e.p.x, e.p.y, size.r + size.halo, 0, Math.PI * 2);
  ctx.fillStyle = PAPER;
  ctx.fill();
  ctx.restore();
}

/**
 * A pinpoint — a test's or a shot's: a white centre as wide as a line, ringed in its colour at a
 * line's weight (2026-10-23, Ricky: a line round the white dot, nothing in it; 2026-10-24: every
 * pinpoint so, the tests' too). Its leader stops at the ring (the centre is paper).
 */
function pinpoint(ctx: CanvasRenderingContext2D, p: { x: number; y: number }, m: Metrics, color: string, size: DotSize): void {
  if (size.r <= 0) return;
  const w = Math.min(m.leader, (2 * size.r) / 3);
  ctx.save();
  ctx.beginPath();
  ctx.arc(p.x, p.y, size.r - w / 2, 0, Math.PI * 2);
  ctx.fillStyle = PAPER;
  ctx.fill();
  ctx.strokeStyle = color;
  ctx.lineWidth = w;
  ctx.stroke();
  ctx.restore();
}

/** An elevation shot's mark: its pinpoint in the tags' colour. */
export function drawElevMarker(ctx: CanvasRenderingContext2D, e: Elevation, m: Metrics, color: string, selected: boolean, viewScale = 1, glow: GlowState = GLOW_REST, size: DotSize = fullDot(m)): void {
  pinpoint(ctx, e.p, m, color, size);
  if (selected) glowDot(ctx, e.p, size, m, viewScale, glow);
}

/* --------------------------------------------------------------- legend */

/**
 * The legend, in the plan's lower left corner (`legendLayout`, labels.ts: the layout keeps a soils
 * map's labels off it). A soils map's is one with its markup: the map's smaller type, a line a box
 * high, a 6u corner and the badges and tags themselves for swatches; a concrete map's is as it was.
 */
export function drawLegend(ctx: CanvasRenderingContext2D, doc: MapDoc, m: Metrics, given?: LegendLayout | null): Rect | null {
  let lg = given;
  if (lg === undefined) {
    ctx.save();
    lg = legendLayout(doc, m, createMeasurer(ctx));
    ctx.restore();
  }
  if (!lg) return null;
  const soils = m.kind === 'soils';
  const { rect: r, size, row, pad, swatchW: sw, gap } = lg;
  ctx.save();
  roundRectPath(ctx, r, lg.radius);
  ctx.globalAlpha = soils ? 1 : 0.95;
  ctx.fillStyle = PAPER;
  ctx.fill();
  ctx.globalAlpha = 1;
  ctx.strokeStyle = LEGEND_BORDER;
  ctx.lineWidth = 1 * m.u;
  ctx.stroke();
  let y = r.y + pad;
  lg.entries.forEach((e, i) => {
    const cy = y + row / 2;
    const sx = r.x + pad;
    const own = lg.swatches[i];
    ctx.save();
    if (own?.badge) {
      // the badge itself, small
      const box = { ...own, rect: { ...own.rect, x: sx, y: cy - own.rect.h / 2 } };
      drawBadge(ctx, box, { disc: e.color, border: e.color, text: INK, fillAlpha: 1 }, m, m.pillRadius * (own.size / m.font));
    } else if (own?.tag) {
      drawElevTag(ctx, { ...own, rect: { ...own.rect, x: sx, y: cy - own.rect.h / 2 } }, e.color, m, m.pillRadius * (own.size / m.font));
    } else if (soils && e.swatch === 'area') {
      const high = 0.85 * row;
      const sr = { x: sx, y: cy - 0.35 * high, w: 1.25 * high, h: 0.7 * high };
      roundRectPath(ctx, sr, 0.2 * sr.h);
      ctx.globalAlpha = 0.45;
      ctx.fillStyle = e.color;
      ctx.fill();
      ctx.globalAlpha = 1;
      ctx.strokeStyle = e.stroke ?? FAIL;
      ctx.lineWidth = 1 * m.u;
      ctx.stroke();
    } else if (soils && e.swatch === 'retest') {
      const f = (0.85 * row) / m.boxRatio;
      ctx.fillStyle = INK;
      ctx.font = fontString(700, f);
      ctx.textBaseline = 'alphabetic';
      ctx.fillText('R', sx + 0.3 * f, cy + 0.36 * f);
    } else if (e.swatch === 'marker' || e.swatch === 'fail') {
      // a badge: the rounded square with a number in it
      const s = sw * 0.72;
      roundRectPath(ctx, { x: sx + (sw - s) / 2, y: cy - s / 2, w: s, h: s }, 0.2 * s);
      ctx.fillStyle = e.color;
      ctx.fill();
      ctx.fillStyle = PAPER;
      ctx.font = fontString(700, 0.62 * s);
      ctx.textAlign = 'center';
      ctx.textBaseline = 'alphabetic';
      ctx.fillText(e.swatch === 'fail' ? '2' : '1', sx + sw / 2, cy + 0.22 * s);
      ctx.textAlign = 'left';
    } else if (e.swatch === 'elev') {
      // a tag in small: its colour, and a ring beside it for the shot
      const th = sw * 0.62;
      roundRectPath(ctx, { x: sx, y: cy - th / 2, w: sw, h: th }, 0.22 * th);
      ctx.fillStyle = e.color;
      ctx.fill();
      ctx.fillStyle = elevInk(e.color);
      ctx.font = fontString(700, 0.62 * th);
      ctx.textAlign = 'center';
      ctx.textBaseline = 'alphabetic';
      ctx.fillText('#', sx + sw / 2, cy + 0.22 * th);
      ctx.textAlign = 'left';
    } else if (e.swatch === 'retest') {
      ctx.fillStyle = INK;
      ctx.font = fontString(700, 0.5 * sw);
      ctx.textBaseline = 'alphabetic';
      ctx.fillText('R', sx + sw * 0.3, cy + 0.18 * sw);
    } else if (e.swatch === 'area') {
      roundRectPath(ctx, { x: sx, y: cy - sw * 0.35, w: sw, h: sw * 0.7 }, sw * 0.12);
      ctx.globalAlpha = 0.45;
      ctx.fillStyle = e.color;
      ctx.fill();
      ctx.globalAlpha = 1;
      ctx.strokeStyle = e.stroke ?? FAIL;
      ctx.lineWidth = 0.8 * m.u;
      ctx.stroke();
    } else {
      roundRectPath(ctx, { x: sx, y: cy - sw * 0.3, w: sw, h: sw * 0.6 }, sw * 0.3);
      ctx.globalAlpha = BAND_ALPHA;
      ctx.fillStyle = e.color;
      ctx.fill();
      ctx.globalAlpha = 1;
      ctx.strokeStyle = e.color;
      ctx.lineWidth = 0.8 * m.u;
      ctx.stroke();
    }
    ctx.restore();
    ctx.fillStyle = INK;
    ctx.font = fontString(600, size);
    ctx.textBaseline = 'alphabetic';
    ctx.fillText(e.text, sx + sw + gap, cy + size * 0.36);
    y += row;
  });
  if (lg.note) {
    ctx.fillStyle = '#4A4A4A';
    ctx.font = fontString(500, lg.noteSize);
    ctx.textBaseline = 'alphabetic';
    ctx.fillText(lg.note, r.x + pad, soils ? y + row / 2 + 0.36 * lg.noteSize : y + row * 0.55 + size * 0.3);
  }
  ctx.restore();
  return r;
}

/* ------------------------------------------------------------ annotations */

/** Leaders, markers, labels, callouts, notes and legend (everything above the shapes). */
export function drawAnnotations(ctx: CanvasRenderingContext2D, doc: MapDoc, m: Metrics, layout: Layout, opts: RenderOptions = {}): void {
  const sel = opts.selection ?? new Set<string>();
  const ui = opts.interactive === true;
  const vs = opts.viewScale ?? 1;
  const glow = opts.glow ?? GLOW_REST;
  const tests = doc.items.filter((i): i is TestPoint => i.t === 'test');
  const elevs = doc.items.filter((i): i is Elevation => i.t === 'elev');
  const callouts = doc.items.filter((i): i is Callout => i.t === 'callout');
  const notes = doc.items.filter((i): i is Note => i.t === 'note');
  const tagColor = elevColor(doc.elev?.color);

  // 0. The white casings of every leader, arrowhead and box, under them all and cut back from every
  // mark (the one in the hand has its own, lifted with it).
  const held = opts.carried;
  // a label carried over a place it may not rest keeps its leaders, faded: still seen, plainly not settled
  const fadeOf = (id: string) => (held?.id === id ? 1 - ADRIFT_FADE * held.adrift : 1);
  const allLeaders = [...layout.soilsLeaders.values(), ...[...layout.callouts.values()].map((b) => b.leaders)].flat().map((r) => r.pts);
  const allBoxes = [...layout.tests.values(), ...layout.elevs.values(), ...layout.callouts.values(), ...layout.notes.values(), ...layout.shared.values()].map((b) => b.rect);
  const noteTips = notes.flatMap((n) => (n.target ? [[n.target]] : []));
  casings(ctx, doc, m, coverOf(allBoxes, [...allLeaders, ...noteTips]), (c) => {
    const lead = (id: string, routes: readonly Routed[] | undefined, heads: boolean) => {
      const fade = fadeOf(id);
      if (!routes?.length || fade <= 0) return;
      c.globalAlpha = fade;
      caseLeaders(c, routes, m, heads);
      c.globalAlpha = 1;
    };
    for (const id of layout.shared.keys()) lead(id, layout.soilsLeaders.get(id), true);
    for (const cl of callouts) lead(cl.id, layout.callouts.get(cl.id)?.leaders, true);
    for (const n of notes) {
      const box = layout.notes.get(n.id);
      if (box && n.target) lead(n.id, [leaderGeometry(box.rect, n.target, m, 0)], true);
    }
    const boxCase = (id: string, box: LabelBox | null | undefined, radius: number, border: number) => {
      if (box && held?.id !== id) caseBox(c, box.rect, radius, border, m);
    };
    for (const [id, b] of layout.shared) boxCase(id, b, m.pillRadius, m.pillBorder);
    for (const t of tests) {
      const b = layout.tests.get(t.id);
      if (b) boxCase(t.id, b, labelRadius(b, m), b.badge ? badgeEdge(b, m) : m.pillBorder);
    }
    for (const e of elevs) boxCase(e.id, layout.elevs.get(e.id), m.pillRadius, 0);
    for (const cl of callouts) boxCase(cl.id, layout.callouts.get(cl.id), m.calloutRadius, m.calloutBorder);
    for (const n of notes) boxCase(n.id, layout.notes.get(n.id), m.pillRadius, m.calloutBorder);
  });

  // 0½. The tests' leaders' casings lie over everything — a mark's band too: a line into a dot inside
  // an area crosses the area's edge with its white border whole, all the way to the dot (the casing
  // layer above is cut back from the bands, for the arrowheads that stop at them). Then the dots'
  // white rings, under the leaders: a test's leader runs on into its dot, so the ring opens for it
  // and the line's casing and the ring are one.
  for (const t of [...tests, ...elevs]) {
    const routes = layout.soilsLeaders.get(t.id);
    const fade = fadeOf(t.id);
    if (!routes?.length || fade <= 0) continue;
    ctx.save();
    ctx.globalAlpha = fade;
    caseLeaders(ctx, routes, m, false);
    ctx.restore();
  }
  for (const t of tests) drawMarkerHalo(ctx, t, m, layout.dot);
  for (const e of elevs) drawElevHalo(ctx, e, layout.elevDot);

  // 1. Leaders (under everything so labels/markers cover their ends cleanly). A label in the hand
  // lets go of its leaders as it goes adrift. The shared work area label's arrows are each in the
  // colour of the area they point at.
  const soilsLeaders = (id: string, color: string | ((r: SoilsLeader) => string), arrows: boolean) => {
    const routes = layout.soilsLeaders.get(id);
    const fade = fadeOf(id);
    if (!routes?.length || fade <= 0) return;
    const colorOf = typeof color === 'string' ? () => color : color;
    ctx.save();
    ctx.globalAlpha = fade;
    if (arrows) for (const r of routes) paintLeaders(ctx, [r], colorOf(r), m);
    else for (const r of routes) strokeRouted(ctx, r, colorOf(r), m);
    ctx.restore();
  };
  for (const id of layout.shared.keys()) {
    const areaArrow = (r: SoilsLeader): string => {
      const a = r.of ? doc.items.find((i) => i.id === r.of) : undefined;
      return a?.t === 'area' ? areaStroke(a) : sharedLabelColor(doc, id);
    };
    soilsLeaders(id, id === FAIL_LABEL ? FAIL : areaArrow, true);
  }
  for (const t of tests) soilsLeaders(t.id, t.status === 'fail' ? FAIL : PASS, false);
  for (const e of elevs) soilsLeaders(e.id, tagColor, false);
  for (const c of callouts) {
    const box = layout.callouts.get(c.id);
    if (!box) continue;
    if (opts.carried?.id === c.id) {
      if (!box.leaders.length) continue;
      ctx.save();
      ctx.globalAlpha = fadeOf(c.id);
      paintLeaders(ctx, box.leaders, calloutColor(doc, c), m);
      ctx.restore();
    } else paintLeaders(ctx, box.leaders, calloutColor(doc, c), m);
  }
  for (const n of notes) {
    const box = layout.notes.get(n.id);
    if (!box || !n.target) continue;
    const color = noteColor(n.color);
    const g = leaderGeometry(box.rect, n.target, m, 0);
    paintLeader(ctx, g, color, m);
  }

  // 2. Selection glow for shapes (under their handles, over the leaders).
  if (ui) {
    for (const it of doc.items) {
      if ((it.t === 'area' || it.t === 'hl') && sel.has(it.id)) drawShapeGlow(ctx, it.shape, m, vs, it.t === 'area', glow);
    }
  }

  // 3. Markers. Every dot is one size, however close tests stand: two that overlap (taken a hand's
  // width apart) are told apart by a hair of white where the later lies on the earlier.
  const placedDots: { p: { x: number; y: number }; r: number }[] = [];
  const parted = (p: { x: number; y: number }, size: DotSize) => {
    const under = placedDots.filter((d) => dist(d.p, p) < d.r + size.r + size.halo);
    if (under.length && size.r > 0) {
      ctx.save();
      ctx.beginPath();
      for (const d of under) {
        ctx.moveTo(d.p.x + d.r, d.p.y);
        ctx.arc(d.p.x, d.p.y, d.r, 0, Math.PI * 2);
      }
      ctx.clip();
      ctx.beginPath();
      ctx.arc(p.x, p.y, size.r + size.halo / 2, 0, Math.PI * 2);
      ctx.strokeStyle = PAPER;
      ctx.lineWidth = size.halo;
      ctx.stroke();
      ctx.restore();
    }
    placedDots.push({ p, r: size.r });
  };
  for (const t of tests) {
    parted(t.p, layout.dot);
    drawMarker(ctx, t, m, ui && sel.has(t.id), vs, glow, layout.dot);
  }
  for (const e of elevs) {
    parted(e.p, layout.elevDot);
    drawElevMarker(ctx, e, m, tagColor, ui && sel.has(e.id), vs, glow, layout.elevDot);
  }

  // 4. Labels (the one in the hand: drawn last, above everything).
  const areaStyle = (id: string): PillStyle => ({ fill: PAPER, fillAlpha: 0.94, border: sharedLabelColor(doc, id), borderWidth: m.pillBorder, text: INK, accent: null, radius: m.pillRadius });
  const failStyle: PillStyle = { fill: PAPER, fillAlpha: 0.94, border: FAIL, borderWidth: m.pillBorder, text: darkOf(FAIL), accent: null, radius: m.pillRadius };
  // a test's badge: passing green, failing red (a test is passing unless it is marked failing)
  const testStyle = (t: TestPoint, box: LabelBox): LabelStyle => {
    const fail = t.status === 'fail';
    const colour = fail ? FAIL : PASS;
    if (box.badge) return { disc: colour, border: colour, text: INK, fillAlpha: 0.96 };
    return { fill: fail ? FAIL : PAPER, fillAlpha: fail ? 1 : 0.94, border: colour, borderWidth: m.pillBorder, text: fail ? PAPER : INK, accent: null, radius: m.pillRadius };
  };
  let lifted: { box: LabelBox; style: LabelStyle; id: string } | null = null;
  const label = (id: string, box: LabelBox | null | undefined, style: LabelStyle) => {
    if (!box) return;
    if (held?.id === id) {
      lifted = { box, style, id };
      return;
    }
    if (ui && sel.has(id)) glowRect(ctx, box.rect, labelRadius(box, m), isBadge(style) ? badgeEdge(box, m) : isTag(style) ? 0 : style.borderWidth, m, vs, glow);
    drawLabelBox(ctx, box, style, m);
  };
  for (const [id, b] of layout.shared) label(id, b, id === FAIL_LABEL ? failStyle : areaStyle(id));
  for (const t of tests) {
    const b = layout.tests.get(t.id);
    if (b) label(t.id, b, testStyle(t, b));
  }
  for (const e of elevs) label(e.id, layout.elevs.get(e.id), { tag: tagColor });
  for (const c of callouts) {
    const box = layout.callouts.get(c.id);
    if (!box) continue;
    if (opts.carried?.id === c.id) continue; // in the hand: drawn last, above everything
    // being typed into: the in-place editor (lifted off the plan, no ring) shows the words
    if (ui && sel.has(c.id) && !box.editing) glowRect(ctx, box.rect, m.calloutRadius, m.calloutBorder, m, vs, glow);
    drawCalloutPill(ctx, box.editing ? { ...box, lines: [], rich: { lines: [], w: 0, size: box.size } } : box, calloutColor(doc, c), m);
  }
  for (const n of notes) {
    const box = layout.notes.get(n.id);
    if (!box) continue;
    const color = noteColor(n.color);
    if (ui && sel.has(n.id)) glowRect(ctx, box.rect, m.pillRadius, m.calloutBorder, m, vs, glow);
    drawPill(ctx, box, { fill: PAPER, fillAlpha: 0.94, border: color, borderWidth: m.calloutBorder, text: INK, accent: null, radius: m.pillRadius }, m);
  }

  // 4½. A selected callout's or shared label's arrowheads are ringed as a selected pinpoint is (2026-10-24,
  // Adam), beyond their white casing; the one in the hand, more strongly.
  if (ui) {
    const ring = (r: { tip: { x: number; y: number }; dir: { x: number; y: number } }, strong: boolean) => {
      const back = add(r.tip, mul(r.dir, -m.arrowLen));
      const n = { x: -r.dir.y, y: r.dir.x };
      const pts = [r.tip, add(back, mul(n, m.arrowWidth / 2)), add(back, mul(n, -m.arrowWidth / 2))];
      const x0 = Math.min(...pts.map((p) => p.x)),
        y0 = Math.min(...pts.map((p) => p.y));
      const bounds = { x: x0, y: y0, w: Math.max(...pts.map((p) => p.x)) - x0, h: Math.max(...pts.map((p) => p.y)) - y0 };
      glowOutline(
        ctx,
        (c) => {
          c.moveTo(pts[0]!.x, pts[0]!.y);
          c.lineTo(pts[1]!.x, pts[1]!.y);
          c.lineTo(pts[2]!.x, pts[2]!.y);
          c.closePath();
        },
        bounds,
        m.leaderHalo,
        vs,
        m,
        glow,
        strong,
      );
    };
    const heldArrow = opts.heldArrow;
    for (const c of callouts) {
      const box = layout.callouts.get(c.id);
      if (!box || held?.id === c.id) continue;
      const on = sel.has(c.id);
      for (const r of box.leaders) {
        const strong = heldArrow?.id === c.id && heldArrow.arrow === r.arrow;
        if (on || strong) ring(r, strong);
      }
    }
    for (const id of layout.shared.keys()) {
      if (held?.id === id) continue;
      const on = sel.has(id);
      for (const r of layout.soilsLeaders.get(id) ?? []) {
        const strong = heldArrow?.id === id && heldArrow.arrow === r.of;
        if (on || strong) ring(r, strong);
      }
    }
  }

  // 4¾. The selected box's grips, to make it narrower or wider.
  if (ui && opts.grip) drawGrips(ctx, opts.grip, m, vs, glow);
  // 4⅞. The selected test's or shot's pinpoint once more, over everything: its ring is sized on the
  // screen, and far out its own label (and the label's grip) would lie over it
  if (ui) {
    for (const t of tests) if (sel.has(t.id)) {
      drawMarkerHalo(ctx, t, m, layout.dot);
      drawMarker(ctx, t, m, true, vs, glow, layout.dot);
    }
    for (const e of elevs) if (sel.has(e.id)) {
      drawElevHalo(ctx, e, layout.elevDot);
      drawElevMarker(ctx, e, m, tagColor, true, vs, glow, layout.elevDot);
    }
  }

  // 5. Legend.
  if (doc.legend) drawLegend(ctx, doc, m, layout.legend);

  // 6. The callout or label in the hand, above it all.
  const heldCallout = held ? callouts.find((c) => c.id === held.id) : undefined;
  const heldBox = heldCallout ? layout.callouts.get(heldCallout.id) : undefined;
  if (held && heldCallout && heldBox) {
    const color = calloutColor(doc, heldCallout);
    drawCarried(ctx, heldBox, { fill: PAPER, fillAlpha: 1, border: color, borderWidth: m.calloutBorder, text: darkOf(color), accent: null, radius: m.calloutRadius }, m, held, ui && sel.has(heldCallout.id), vs, glow);
  }
  const up = lifted as { box: LabelBox; style: LabelStyle; id: string } | null;
  if (held && up) drawCarried(ctx, up.box, up.style, m, held, ui && sel.has(up.id), vs, glow);
}

const ADRIFT = '#8C919A'; // what a callout's colour drains towards where it may not rest

/**
 * An arrow shaken off its mark, drawn as it goes: its leader (`pts`, as routed: the box's end first,
 * the tip last) kept only for `keep` (1 … 0) of its length from the box, its white casing under it
 * and a head on its new end along the line's last direction — the head shrinking as the line runs
 * out — all at `alpha`. (The editor's overlay calls it frame by frame while the arrow retracts.)
 */
export function paintArrowGhost(ctx: CanvasRenderingContext2D, pts: readonly { x: number; y: number }[], color: string, m: Metrics, keep: number, alpha: number): void {
  if (pts.length < 2 || keep <= 0 || alpha <= 0) return;
  let total = 0;
  for (let i = 0; i + 1 < pts.length; i++) total += Math.hypot(pts[i + 1]!.x - pts[i]!.x, pts[i + 1]!.y - pts[i]!.y);
  const want = Math.min(1, keep) * total;
  const line: { x: number; y: number }[] = [pts[0]!];
  let walked = 0;
  let dir = { x: 0, y: 0 };
  for (let i = 0; i + 1 < pts.length; i++) {
    const a = pts[i]!,
      b = pts[i + 1]!;
    const l = Math.hypot(b.x - a.x, b.y - a.y);
    if (l < 1e-6) continue;
    dir = { x: (b.x - a.x) / l, y: (b.y - a.y) / l };
    if (walked + l >= want) {
      line.push({ x: a.x + dir.x * (want - walked), y: a.y + dir.y * (want - walked) });
      break;
    }
    line.push(b);
    walked += l;
  }
  const tip = line[line.length - 1]!;
  // the head: its full size while the line is long enough to carry it, then less and less
  const k = Math.max(0, Math.min(1, want / (HEAD_RUN * m.arrowLen)));
  const len = m.arrowLen * k,
    half = (m.arrowWidth / 2) * k;
  const back = { x: tip.x - dir.x * len, y: tip.y - dir.y * len };
  const head = [tip, { x: back.x - dir.y * half, y: back.y + dir.x * half }, { x: back.x + dir.y * half, y: back.y - dir.x * half }];
  // the line ends inside the head
  const body = line.slice(0, -1).concat([{ x: tip.x - dir.x * 0.45 * len, y: tip.y - dir.y * 0.45 * len }]);
  const path = (c: CanvasRenderingContext2D, ps: readonly { x: number; y: number }[], close = false) => {
    c.beginPath();
    c.moveTo(ps[0]!.x, ps[0]!.y);
    for (const p of ps.slice(1)) c.lineTo(p.x, p.y);
    if (close) c.closePath();
  };
  ctx.save();
  ctx.globalAlpha *= alpha;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  // casing, then the line and its head
  ctx.strokeStyle = PAPER;
  ctx.fillStyle = PAPER;
  ctx.lineWidth = m.leader + 2 * m.leaderHalo;
  path(ctx, body);
  ctx.stroke();
  if (k > 0) {
    ctx.lineWidth = 2 * m.leaderHalo;
    path(ctx, head, true);
    ctx.fill();
    ctx.stroke();
  }
  ctx.strokeStyle = color;
  ctx.fillStyle = color;
  ctx.lineWidth = m.leader;
  path(ctx, body);
  ctx.stroke();
  if (k > 0) {
    path(ctx, head, true);
    ctx.fill();
  }
  ctx.restore();
}
/** How much of a carried label's leaders fades where it may not rest: they stay, at a third — seen, not settled. */
const ADRIFT_FADE = 0.65;

/** A box in the hand: lifted on a soft shadow, its colours draining towards grey as it goes adrift. */
function drawCarried(ctx: CanvasRenderingContext2D, box: LabelBox, st: LabelStyle, m: Metrics, c: NonNullable<RenderOptions['carried']>, selected: boolean, vs: number, glow: GlowState): void {
  const { lift, adrift, dpr } = c;
  const radius = labelRadius(box, m);
  const border = isBadge(st) ? badgeEdge(box, m) : isTag(st) ? 0 : st.borderWidth;
  // the shadow of the box alone (canvas shadows are in device px, whatever the zoom)
  if (lift > 0.01) {
    ctx.save();
    ctx.shadowColor = `rgba(10, 20, 40, ${0.26 * lift})`;
    ctx.shadowBlur = 28 * dpr * lift;
    ctx.shadowOffsetY = 10 * dpr * lift;
    roundRectPath(ctx, box.rect, radius);
    ctx.fillStyle = PAPER;
    ctx.fill();
    ctx.restore();
  }
  // its white casing, lifted with it (it fades as the box goes adrift: over a mark there is none)
  if (adrift < 1) {
    ctx.save();
    ctx.globalAlpha = 1 - adrift;
    caseBox(ctx, box.rect, radius, border, m);
    ctx.restore();
  }
  if (selected) glowRect(ctx, box.rect, radius, border, m, vs, { ...glow, alpha: glow.alpha * (1 - adrift) });
  const drain = (hex: string, k: number) => (hex === PAPER ? PAPER : mixHex(hex, ADRIFT, k * adrift));
  if (isBadge(st)) {
    drawBadge(ctx, box, { disc: drain(st.disc, 0.6), border: drain(st.border, 0.75), text: drain(st.text, 0.6), fillAlpha: Math.min(1, st.fillAlpha + 0.04) - 0.26 * adrift }, m);
    return;
  }
  if (isTag(st)) {
    drawElevTag(ctx, box, drain(st.tag, 0.6), m);
    return;
  }
  drawPill(ctx, box, { ...st, fill: drain(st.fill, 0.6), fillAlpha: Math.min(1, st.fillAlpha + 0.06) - 0.26 * adrift, border: drain(st.border, 0.75), text: drain(st.text, 0.6), accent: st.accent ? drain(st.accent, 0.6) : null }, m);
}

/** How a test's badge is coloured: its square, and its tail's border and words. */
interface BadgeStyle {
  disc: string;
  border: string;
  text: string;
  fillAlpha: number;
}
/** How an elevation shot's tag is coloured: the map's tag colour behind white type. */
interface TagStyle {
  tag: string;
}
type LabelStyle = PillStyle | BadgeStyle | TagStyle;
function isBadge(st: LabelStyle): st is BadgeStyle {
  return 'disc' in st;
}
function isTag(st: LabelStyle): st is TagStyle {
  return 'tag' in st;
}
/** A label box as itself: a test's badge, a shot's tag, or a pill of words. */
function drawLabelBox(ctx: CanvasRenderingContext2D, box: LabelBox, st: LabelStyle, m: Metrics): void {
  if (isBadge(st)) {
    if (box.badge) drawBadge(ctx, box, st, m);
  } else if (isTag(st)) drawElevTag(ctx, box, st.tag, m);
  else drawPill(ctx, box, st, m);
}

/**
 * An elevation shot's tag: one rounded box of the tags' colour, cornered as every box is, the number
 * and then the elevation bold and white in it — "#1  Elev. 2795.65" — all of it at full strength.
 */
export function drawElevTag(ctx: CanvasRenderingContext2D, box: LabelBox, color: string, m: Metrics, radius = m.pillRadius): void {
  const t = box.tag;
  if (!t) return;
  const r = box.rect;
  ctx.save();
  roundRectPath(ctx, r, radius);
  ctx.fillStyle = color;
  ctx.fill();
  // white type — ink on a colour of one's own too light for white
  ctx.fillStyle = elevInk(color);
  ctx.textAlign = 'left';
  ctx.textBaseline = 'alphabetic';
  const y = r.y + t.base;
  ctx.font = fontString(700, box.size);
  ctx.fillText(t.n, r.x + t.padX, y);
  // its words after its number (in a column of their own when they wrap), the note in its own look
  const ink = elevInk(color);
  if (t.words) drawRich(ctx, t.wordsText, r.x + t.wordsX, r.y + t.wordsTop, t.line, r.w - t.wordsX - t.padX, box.align ?? 'left', ink);
  if (t.depth && t.depthSize && t.depthBase) {
    ctx.fillStyle = ink;
    ctx.font = fontString(600, t.depthSize);
    ctx.fillText(t.depth, r.x + t.wordsX, r.y + t.depthBase);
  }
  ctx.restore();
}

/** A badge's border: only a badge with a tail has one (the tail's, in the test's colour); a plain badge is its block alone. */
function badgeEdge(box: LabelBox, m: Metrics): number {
  return box.badge?.tail ? m.badgeBorder : 0;
}

/**
 * A test's badge: its name bold and white in a rounded block of the test's colour — passing green,
 * failing red — in the size of every other word on the map, cornered as every box on the map is;
 * with words to add, a white tail
 * the block's height runs on from it in the same rounded rectangle, bordered in the test's colour,
 * the words dark in it. Its white casing is on the casing layer (`casings`). `radius`: smaller for
 * the legend's badge.
 */
function drawBadge(ctx: CanvasRenderingContext2D, box: LabelBox, st: BadgeStyle, m: Metrics, radius = m.pillRadius): void {
  const b = box.badge;
  if (!b) return;
  const r = box.rect;
  const R = radius;
  const size = box.size;
  ctx.save();
  if (b.tail) {
    roundRectPath(ctx, r, R);
    ctx.globalAlpha = st.fillAlpha;
    ctx.fillStyle = PAPER;
    ctx.fill();
    ctx.globalAlpha = 1;
    ctx.strokeStyle = st.border;
    ctx.lineJoin = 'round';
    // (a line's weight, as every coloured line on the map: thinner only for the legend's small badge)
    ctx.lineWidth = m.badgeBorder * Math.min(1, size / m.font);
    ctx.stroke();
  }
  // the coloured block — the whole badge without a tail; with one, its right corners square where the tail runs on
  const face = { x: r.x, y: r.y, w: b.tail ? b.face : r.w, h: r.h };
  ctx.beginPath();
  if (b.tail && typeof ctx.roundRect === 'function') ctx.roundRect(face.x, face.y, face.w, face.h, [R, 0, 0, R]);
  else roundRectSubpath(ctx, face, R);
  ctx.fillStyle = st.disc;
  ctx.fill();
  // the name centred in the block, its capitals in the block's middle
  ctx.fillStyle = PAPER;
  ctx.textAlign = 'left';
  ctx.textBaseline = 'alphabetic';
  const shift = (face.w - b.face) / 2;
  ctx.font = fontString(700, size);
  ctx.fillText(b.pre + b.n, r.x + shift + b.preX, r.y + r.h / 2 + 0.36 * size);
  if (b.tailText) {
    // the words after it: on its middle, or — wrapped in a badge made narrower by hand — line under line
    const lh = m.lineHeight * b.tailSize;
    const n = b.tailText.lines.length;
    drawRich(ctx, b.tailText, r.x + b.tailX, r.y + (r.h - n * lh) / 2, lh, r.w - b.tailX - m.padRatio * b.tailSize, box.align ?? 'left', st.text);
  }
  ctx.restore();
}

/** A callout box: one border weight all round, in the material's colour. */
export function drawCalloutPill(ctx: CanvasRenderingContext2D, box: LabelBox, color: string, m: Metrics): void {
  drawPill(ctx, box, { fill: PAPER, fillAlpha: 1, border: color, borderWidth: m.calloutBorder, text: darkOf(color), accent: null, radius: m.calloutRadius }, m);
}

export function drawHandles(ctx: CanvasRenderingContext2D, s: Shape, color: string, viewScale: number, selectedIndex: number | null): void {
  const pts = s.k === 'rect' ? rectToPoly(shapeBounds(s)) : s.pts;
  const closed = s.k !== 'stroke';
  const r = 7 / viewScale;
  ctx.save();
  // a path's anchors are exactly its editable points; polygons show their corners
  const shown = s.k === 'path' ? Array.from({ length: pts.length / 2 }, (_, i) => i) : keyVertices(pts, closed, 16 / viewScale);
  for (const i of shown) {
    const x = pts[2 * i]!,
      y = pts[2 * i + 1]!;
    const sel = selectedIndex === i;
    ctx.beginPath();
    ctx.arc(x, y, sel ? r * 1.35 : r, 0, Math.PI * 2);
    ctx.fillStyle = sel ? SELECT : PAPER;
    ctx.fill();
    ctx.strokeStyle = sel ? PAPER : color;
    ctx.lineWidth = (sel ? 2.5 : 2) / viewScale;
    ctx.stroke();
  }
  ctx.restore();
}

/**
 * A drawn shape's handles, a constant size on screen. Handled as a whole (`points` false): its corners
 * (squares; a custom shape's round points circles), a short bar on each side (the side moves square to
 * itself) and a box's or oval's knob. Its points out (Edit): each point, and a dot mid-side to pull a
 * new one out of. A circle: four circles on it; an oval the same and a knob. The point in the hand (or
 * the selected one) is filled. Bars and dots on sides too short to hold them are left out.
 */
export function drawFormHandles(ctx: CanvasRenderingContext2D, f: Form, color: string, viewScale: number, selected: number | null, mids = true, knob: { at: { x: number; y: number }; knob: { x: number; y: number } } | null = null, points = false, roundings: readonly { x: number; y: number }[] = []): void {
  const vs = viewScale;
  const dpr = Math.abs(ctx.getTransform().a) / vs || 1;
  ctx.save();
  const corners = formHandles(f);
  drawRoundingHandles(ctx, roundings, color, vs);
  if (knob) {
    ctx.beginPath();
    ctx.moveTo(knob.at.x, knob.at.y);
    ctx.lineTo(knob.knob.x, knob.knob.y);
    ctx.strokeStyle = color;
    ctx.lineWidth = 1.5 / vs;
    ctx.stroke();
    ctx.beginPath();
    ctx.arc(knob.knob.x, knob.knob.y, 8 / vs, 0, Math.PI * 2);
    ctx.shadowColor = 'rgba(0,0,0,0.3)';
    ctx.shadowBlur = 3 * dpr;
    ctx.shadowOffsetY = 1 * dpr;
    ctx.fillStyle = PAPER;
    ctx.fill();
    ctx.shadowColor = 'transparent';
    ctx.lineWidth = 2 / vs;
    ctx.stroke();
    // a turning arrow in it
    ctx.beginPath();
    ctx.arc(knob.knob.x, knob.knob.y, 4 / vs, -Math.PI * 0.9, Math.PI * 0.35);
    ctx.lineWidth = 1.5 / vs;
    ctx.stroke();
  }
  if (mids) {
    const M = formMids(f);
    M.forEach((q, i) => {
      const a = corners[i]!,
        b = corners[(i + 1) % corners.length]!;
      const side = Math.hypot(b.x - a.x, b.y - a.y) * vs;
      if (f.f === 'ellipse') return;
      // a square's sides are bars (the side moves square to itself); a custom shape's are dots (pulled, a new point)
      if (!points && f.f === 'rect') {
        if (side < 56) return;
        // a short bar along the side: the side moves square to itself
        const ux = (b.x - a.x) / (side / vs),
          uy = (b.y - a.y) / (side / vs);
        const L = 9 / vs,
          T = 3.2 / vs;
        ctx.save();
        ctx.translate(q.x, q.y);
        ctx.rotate(Math.atan2(uy, ux));
        ctx.beginPath();
        roundRectSubpath(ctx, { x: -L, y: -T, w: 2 * L, h: 2 * T }, T);
        ctx.shadowColor = 'rgba(0,0,0,0.3)';
        ctx.shadowBlur = 3 * dpr;
        ctx.shadowOffsetY = 1 * dpr;
        ctx.fillStyle = PAPER;
        ctx.fill();
        ctx.shadowColor = 'transparent';
        ctx.lineWidth = 1.8 / vs;
        ctx.strokeStyle = color;
        ctx.stroke();
        ctx.restore();
        return;
      }
      if (side < 40) return;
      ctx.beginPath();
      ctx.arc(q.x, q.y, 4 / vs, 0, Math.PI * 2);
      ctx.fillStyle = 'rgba(255,255,255,0.92)';
      ctx.fill();
      ctx.lineWidth = 1.5 / vs;
      ctx.strokeStyle = color;
      ctx.globalAlpha = 0.85;
      ctx.stroke();
      ctx.globalAlpha = 1;
    });
  }
  corners.forEach((q, i) => {
    const on = selected === i;
    const round = f.f === 'ellipse' || (f.f === 'poly' && !!f.round[i]);
    const r = (on ? 7.5 : 6) / vs;
    ctx.beginPath();
    if (round) ctx.arc(q.x, q.y, r, 0, Math.PI * 2);
    else roundRectSubpath(ctx, { x: q.x - r, y: q.y - r, w: 2 * r, h: 2 * r }, 2 / vs);
    ctx.shadowColor = 'rgba(0,0,0,0.3)';
    ctx.shadowBlur = 3 * dpr;
    ctx.shadowOffsetY = 1 * dpr;
    ctx.fillStyle = on ? color : PAPER;
    ctx.fill();
    ctx.shadowColor = 'transparent';
    ctx.lineWidth = 2 / vs;
    ctx.strokeStyle = on ? PAPER : color;
    ctx.stroke();
  });
  ctx.restore();
}

/** Rounded corners' handles: a small ring each, just inside its arc (pulled in or out, the corner rounds more or less). */
export function drawRoundingHandles(ctx: CanvasRenderingContext2D, at: readonly { x: number; y: number }[], color: string, vs: number): void {
  if (!at.length) return;
  const dpr = Math.abs(ctx.getTransform().a) / vs || 1;
  ctx.save();
  for (const q of at) {
    ctx.beginPath();
    ctx.arc(q.x, q.y, 5.5 / vs, 0, Math.PI * 2);
    ctx.shadowColor = 'rgba(0,0,0,0.3)';
    ctx.shadowBlur = 3 * dpr;
    ctx.shadowOffsetY = 1 * dpr;
    ctx.fillStyle = PAPER;
    ctx.fill();
    ctx.shadowColor = 'transparent';
    ctx.lineWidth = 1.8 / vs;
    ctx.strokeStyle = color;
    ctx.stroke();
    ctx.beginPath();
    ctx.arc(q.x, q.y, 2 / vs, 0, Math.PI * 2);
    ctx.fillStyle = color;
    ctx.fill();
  }
  ctx.restore();
}

/** What a shape being placed with the Pen shows (see drawPen). */
export interface PenDrawing {
  /** The points placed, with the one being carried where it is now (and the one being placed last). */
  pts: readonly { x: number; y: number }[];
  round: readonly number[];
  r: readonly number[];
  /** The shape it will be (three points or more). */
  form: PolyForm | null;
  /** The point being placed (the last of `pts`) and whether it holds the plan. */
  placing: boolean;
  snapped: boolean;
  /** The point in hand. */
  sel: number | null;
  /** A stretch being drawn (Draw). */
  stroke: readonly number[] | null;
  guides: GuideSnap | null;
  roundings: readonly { x: number; y: number }[];
  /** The middles of the sides placed (pulled, a new point). */
  mids: readonly { x: number; y: number }[];
}

/**
 * A shape being placed with the Pen: the outline so far exactly as it will be (curves, rounded
 * corners), closing back to the first point with a dashed side, the body faintly filled; each point
 * a handle — a corner a square, a point it curves through a circle, a rounded corner a square with
 * softened corners — the first larger, to close on; the point in hand filled; the one being placed
 * lit while it holds the plan; the guides it has settled on; and a stretch being drawn.
 */
export function drawPen(ctx: CanvasRenderingContext2D, d: PenDrawing, color: string, fill: string, viewScale: number): void {
  const vs = viewScale;
  const dpr = Math.abs(ctx.getTransform().a) / vs || 1;
  const P = d.pts;
  if (!P.length && !d.stroke) return;
  ctx.save();
  ctx.lineJoin = 'round';
  ctx.lineCap = 'round';
  const line = (width: number, style: string) => {
    ctx.strokeStyle = PAPER;
    ctx.lineWidth = width + 2.5 / vs;
    ctx.stroke();
    ctx.strokeStyle = style;
    ctx.lineWidth = width;
    ctx.stroke();
  };
  if (d.form) {
    const { edges, fillets } = formRuns(d.form);
    const n = edges.length;
    // the body
    ctx.beginPath();
    const start = fillets[0]?.from ?? edges[0]!.from;
    ctx.moveTo(start.x, start.y);
    edges.forEach((e, i) => {
      const fl = fillets[i];
      if (fl) for (const c of fl.cubics) ctx.bezierCurveTo(c.c1.x, c.c1.y, c.c2.x, c.c2.y, c.to.x, c.to.y);
      for (const c of e.cubics) ctx.bezierCurveTo(c.c1.x, c.c1.y, c.c2.x, c.c2.y, c.to.x, c.to.y);
    });
    ctx.closePath();
    ctx.globalAlpha = 0.2;
    ctx.fillStyle = fill;
    ctx.fill();
    ctx.globalAlpha = 1;
    // the outline placed, and the side that will close it (dashed)
    ctx.beginPath();
    ctx.moveTo(start.x, start.y);
    edges.forEach((e, i) => {
      const fl = fillets[i];
      if (fl) for (const c of fl.cubics) ctx.bezierCurveTo(c.c1.x, c.c1.y, c.c2.x, c.c2.y, c.to.x, c.to.y);
      if (i < n - 1) for (const c of e.cubics) ctx.bezierCurveTo(c.c1.x, c.c1.y, c.c2.x, c.c2.y, c.to.x, c.to.y);
    });
    line(2.5 / vs, color);
    const last = edges[n - 1]!;
    ctx.beginPath();
    ctx.moveTo(last.from.x, last.from.y);
    for (const c of last.cubics) ctx.bezierCurveTo(c.c1.x, c.c1.y, c.c2.x, c.c2.y, c.to.x, c.to.y);
    ctx.setLineDash([6 / vs, 5 / vs]);
    ctx.strokeStyle = color;
    ctx.lineWidth = 2 / vs;
    ctx.stroke();
    ctx.setLineDash([]);
  } else if (P.length === 2) {
    ctx.beginPath();
    ctx.moveTo(P[0]!.x, P[0]!.y);
    ctx.lineTo(P[1]!.x, P[1]!.y);
    line(2.5 / vs, color);
  }
  // a stretch being drawn, from the last point
  if (d.stroke && d.stroke.length >= 2) {
    ctx.beginPath();
    const last = P[P.length - 1];
    if (last) ctx.moveTo(last.x, last.y);
    for (let i = 0; i + 1 < d.stroke.length; i += 2) (i || last ? ctx.lineTo(d.stroke[i]!, d.stroke[i + 1]!) : ctx.moveTo(d.stroke[i]!, d.stroke[i + 1]!));
    line(2.5 / vs, color);
  }
  // the guides the point has settled on: through it from where each comes, and on a little beyond
  if (d.guides) {
    const g = d.guides;
    ctx.save();
    ctx.setLineDash([4 / vs, 4 / vs]);
    ctx.strokeStyle = SELECT;
    ctx.lineWidth = 1.2 / vs;
    for (const gd of g.guides) {
      const t = (g.p.x - gd.o.x) * gd.u.x + (g.p.y - gd.o.y) * gd.u.y;
      const s = Math.sign(t) || 1;
      ctx.beginPath();
      ctx.moveTo(gd.o.x, gd.o.y);
      ctx.lineTo(g.p.x + gd.u.x * s * (40 / vs), g.p.y + gd.u.y * s * (40 / vs));
      ctx.stroke();
      if (gd.square) {
        // a right angle where it stands square to the side before it
        const k = 9 / vs;
        // (a square guide stands across the side it is square to: back along that side is a quarter turn from it)
        const w = { x: -gd.u.y, y: gd.u.x };
        ctx.setLineDash([]);
        ctx.beginPath();
        ctx.moveTo(gd.o.x + gd.u.x * s * k, gd.o.y + gd.u.y * s * k);
        ctx.lineTo(gd.o.x + gd.u.x * s * k + w.x * k, gd.o.y + gd.u.y * s * k + w.y * k);
        ctx.lineTo(gd.o.x + w.x * k, gd.o.y + w.y * k);
        ctx.stroke();
        ctx.setLineDash([4 / vs, 4 / vs]);
      }
    }
    ctx.restore();
  }
  drawRoundingHandles(ctx, d.roundings, color, vs);
  // the middle of each side placed: a dot to pull a new point out of
  for (const q of d.mids) {
    ctx.beginPath();
    ctx.arc(q.x, q.y, 4 / vs, 0, Math.PI * 2);
    ctx.fillStyle = 'rgba(255,255,255,0.92)';
    ctx.fill();
    ctx.lineWidth = 1.5 / vs;
    ctx.strokeStyle = color;
    ctx.globalAlpha = 0.85;
    ctx.stroke();
    ctx.globalAlpha = 1;
  }
  // the points
  P.forEach((q, i) => {
    const first = i === 0 && P.length - (d.placing ? 1 : 0) >= 3;
    const live = d.placing && i === P.length - 1;
    const on = d.sel === i;
    const curve = !!d.round[i];
    const r = (first ? 8 : live || on ? 7.5 : 6) / vs;
    ctx.beginPath();
    if (curve) ctx.arc(q.x, q.y, r, 0, Math.PI * 2);
    else roundRectSubpath(ctx, { x: q.x - r, y: q.y - r, w: 2 * r, h: 2 * r }, (d.r[i]! > 0 ? 0.6 * r : 2 / vs));
    ctx.shadowColor = 'rgba(0,0,0,0.3)';
    ctx.shadowBlur = 3 * dpr;
    ctx.shadowOffsetY = 1 * dpr;
    ctx.fillStyle = on || (live && d.snapped) ? color : PAPER;
    ctx.fill();
    ctx.shadowColor = 'transparent';
    ctx.lineWidth = 2 / vs;
    ctx.strokeStyle = on || (live && d.snapped) ? PAPER : color;
    ctx.stroke();
    if (first) {
      // the first point: tap it to close
      ctx.beginPath();
      ctx.arc(q.x, q.y, 15 / vs, 0, Math.PI * 2);
      ctx.strokeStyle = color;
      ctx.globalAlpha = 0.45;
      ctx.lineWidth = 2 / vs;
      ctx.stroke();
      ctx.globalAlpha = 1;
    }
  });
  ctx.restore();
}

/** The places the finger rested while sketching: each a corner it placed. */
export function drawRests(ctx: CanvasRenderingContext2D, pts: readonly { x: number; y: number }[], color: string, viewScale: number): void {
  const vs = viewScale;
  ctx.save();
  for (const q of pts) {
    ctx.beginPath();
    ctx.arc(q.x, q.y, 6 / vs, 0, Math.PI * 2);
    ctx.fillStyle = PAPER;
    ctx.fill();
    ctx.lineWidth = 2.5 / vs;
    ctx.strokeStyle = color;
    ctx.stroke();
  }
  ctx.restore();
}

/**
 * An end tab's size on screen, from its end's width on screen (px). It is a part of the mark, so
 * it keeps the mark's proportions: about half as long as the end is wide, never longer than the
 * end itself far out (where it shrinks to a small pip rather than cover the mark), and no bar
 * across the screen close in; its thickness follows its length. The finger's target around it
 * (`reach…`) never gets smaller than a fingertip.
 */
export function endTabSize(widthOnScreen: number): { len: number; th: number; reachAlong: number; reachAcross: number } {
  const len = Math.max(Math.min(16, 0.8 * widthOnScreen), Math.min(0.5 * widthOnScreen, 96));
  const th = Math.max(3.5, Math.min(11, 0.12 * len + 3.2));
  return { len, th, reachAlong: Math.max(22, len / 2 + 12), reachAcross: Math.max(16, th / 2 + 12) };
}
/** Where an end's tab sits: on the middle of the colour band that runs round the mark, not on its inner edge. */
export function endTabCentre(end: { mid: { x: number; y: number }; out: { x: number; y: number } }, m: Metrics): { x: number; y: number } {
  return { x: end.mid.x + (end.out.x * m.bandEdge) / 2, y: end.mid.y + (end.out.y * m.bandEdge) / 2 };
}

/**
 * The tabs at a selected mark's ends: capsules lying along each end, on its colour band, sized
 * with the mark (`endTabSize`). `activeEdge` is the end being pulled, as it is now.
 */
export function drawEndHandles(ctx: CanvasRenderingContext2D, ends: readonly BandEnd[], color: string, m: Metrics, viewScale: number, activeIndex: number | null, activeEdge: { a: { x: number; y: number }; b: { x: number; y: number } } | null = null): void {
  const vs = viewScale;
  const dpr = Math.abs(ctx.getTransform().a) / vs || 1;
  const tabs: { mid: { x: number; y: number }; along: { x: number; y: number }; out: { x: number; y: number }; width: number; active: boolean }[] = ends.map((end, k) => ({ mid: end.mid, along: end.along, out: end.out, width: end.width, active: activeIndex === k }));
  if (activeEdge) {
    // the end being pulled: its tab rides on the edge as it is now, in place of the end it was
    const mid = { x: (activeEdge.a.x + activeEdge.b.x) / 2, y: (activeEdge.a.y + activeEdge.b.y) / 2 };
    const width = Math.hypot(activeEdge.b.x - activeEdge.a.x, activeEdge.b.y - activeEdge.a.y) || 1;
    const along = { x: (activeEdge.b.x - activeEdge.a.x) / width, y: (activeEdge.b.y - activeEdge.a.y) / width };
    // outwards is the edge's normal on the side the end faced
    const was = activeIndex !== null && activeIndex >= 0 && activeIndex < tabs.length ? tabs.splice(activeIndex, 1)[0] : undefined;
    const sign = was && was.out.x * -along.y + was.out.y * along.x < 0 ? -1 : 1;
    tabs.push({ mid, along, out: { x: -along.y * sign, y: along.x * sign }, width, active: true });
  }
  for (const end of tabs) {
    const active = end.active;
    const size = endTabSize(end.width * vs);
    const len = size.len / vs;
    const th = (size.th * (active ? 1.12 : 1)) / vs;
    const c = endTabCentre(end, m);
    ctx.save();
    ctx.translate(c.x, c.y);
    ctx.rotate(Math.atan2(end.along.y, end.along.x));
    ctx.beginPath();
    roundRectSubpath(ctx, { x: -len / 2, y: -th / 2, w: len, h: th }, th / 2);
    // the shadow is in device px whatever the zoom: it follows the tab's size too
    ctx.shadowColor = 'rgba(0, 0, 0, 0.26)';
    ctx.shadowBlur = Math.max(1.5, 0.45 * size.th) * dpr;
    ctx.shadowOffsetY = Math.max(0.5, 0.1 * size.th) * dpr;
    ctx.fillStyle = active ? color : PAPER;
    ctx.fill();
    ctx.shadowColor = 'transparent';
    ctx.strokeStyle = active ? PAPER : color;
    ctx.lineWidth = Math.max(1, Math.min(2, 0.2 * size.th)) / vs;
    ctx.stroke();
    ctx.restore();
  }
}

/**
 * What is about to be cut out of a mark, while it is drawn: the plan under it through a veil of
 * paper (it will no longer be marked), its edge a dashed line, sized on screen.
 */
export function drawCutPreview(ctx: CanvasRenderingContext2D, polys: readonly number[][], viewScale: number): void {
  if (!polys.length) return;
  ctx.save();
  ctx.beginPath();
  for (const p of polys) polyPath(ctx, p, true, false);
  ctx.globalAlpha = 0.55;
  ctx.fillStyle = PAPER;
  ctx.fill();
  ctx.globalAlpha = 1;
  ctx.lineJoin = 'round';
  ctx.strokeStyle = PAPER;
  ctx.lineWidth = 4.5 / viewScale;
  ctx.stroke();
  ctx.setLineDash([7 / viewScale, 5 / viewScale]);
  ctx.strokeStyle = INK;
  ctx.lineWidth = 2 / viewScale;
  ctx.stroke();
  ctx.restore();
}

/**
 * The shapes cut out of a mark while it is being cut into, each as itself: a dashed line on a thin
 * white casing, sized on screen (where they overlap they cut as one; each stays its own shape).
 */
export function drawCutOutlines(ctx: CanvasRenderingContext2D, polys: readonly number[][], viewScale: number): void {
  if (!polys.length) return;
  ctx.save();
  ctx.beginPath();
  for (const p of polys) if (p.length >= 6) polyPath(ctx, p, true, false);
  ctx.lineJoin = 'round';
  ctx.strokeStyle = PAPER;
  ctx.lineWidth = 4 / viewScale;
  ctx.stroke();
  ctx.setLineDash([6 / viewScale, 4 / viewScale]);
  ctx.strokeStyle = INK;
  ctx.lineWidth = 1.6 / viewScale;
  ctx.stroke();
  ctx.restore();
}

/** A soft glow under the finger while the tracer has nothing to show yet. */
export function drawFingerGlow(ctx: CanvasRenderingContext2D, p: { x: number; y: number }, color: string, viewScale: number): void {
  const r = 26 / viewScale;
  const g = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, r);
  g.addColorStop(0, color);
  g.addColorStop(1, 'rgba(0,0,0,0)');
  ctx.save();
  ctx.globalAlpha = 0.45;
  ctx.fillStyle = g;
  ctx.beginPath();
  ctx.arc(p.x, p.y, r, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

/** Snap indicator (a small ring) at a point, screen-sized. */
export function drawSnapRing(ctx: CanvasRenderingContext2D, p: { x: number; y: number }, viewScale: number, kind: 'node' | 'line'): void {
  ctx.save();
  ctx.beginPath();
  ctx.arc(p.x, p.y, (kind === 'node' ? 11 : 8) / viewScale, 0, Math.PI * 2);
  ctx.strokeStyle = SELECT;
  ctx.lineWidth = 2 / viewScale;
  ctx.setLineDash(kind === 'line' ? [3 / viewScale, 3 / viewScale] : []);
  ctx.stroke();
  ctx.restore();
}

/** Full render into a context whose transform maps image px → device px. */
export function renderAll(ctx: CanvasRenderingContext2D, doc: MapDoc, image: CanvasImageSource | null, m: Metrics, layout: Layout, opts: RenderOptions = {}): void {
  if (image) ctx.drawImage(image, 0, 0, doc.w, doc.h);
  else {
    ctx.fillStyle = PAPER;
    ctx.fillRect(0, 0, doc.w, doc.h);
  }
  drawLift(ctx, doc, m, image);
  drawShapes(ctx, doc, m);
  drawAnnotations(ctx, doc, m, layout, opts);
}
